/**
 * Thin v2 wrapper around the user-KG endpoints.
 *
 * We don't reuse the Product axios client (frontend/apps/product/src/api/client.js)
 * directly here because its response interceptor force-redirects to
 * /login on any 401 — which would yank anonymous /next/app/* visitors
 * out of the v2 experience. Instead we use a dedicated v2 client below.
 *
 * Strategy: fetch the live backend with a v2-only client and report the
 * outcome HONESTLY. There is NO mock/demo fallback: on error we return
 * `{status:'error', source:'live', data:null}` and the private screen renders
 * an honest error/empty state. The designer's in-bundle demo bundle is legal
 * ONLY for an anonymous viewer on a PUBLIC surface (`status:'unauthenticated'`,
 * `source:'demo'`). See `KgSource` / `KgStatus` below.
 */
import axios from 'axios';
import { assertOwnedBackendRequest, productApiOrigin, resolveBackendUrl } from '../../../config/apiOrigin';
import { setupAxiosProgramInterceptor } from '../../../api/setupAxios';
import { isAuthLikeToken } from '../../../lib/authMode';
import { expireProductSession } from '../../../lib/sessionExpiry';
import type {
  KgNodeOverlay, KgRecommendationItem, KgEdge, KgEdgeType,
  MasteryBand, KgNodeType,
} from '../../../features/kg/types/productKg.types';
import type { PracticeLaunchEvent } from '../analytics/practiceLaunch';
import { cachedRequest, invalidateRequestCache } from './requestCache';

// ── v2-only axios instance (no global 401 interceptor) ─────────────────────

// Exported so sibling v2 clients (e.g. focusedPracticeClient) can reuse the
// SAME configured instance — and therefore the SAME auth request interceptor +
// 401 silent-refresh-and-retry behavior — instead of re-creating an axios
// instance that would diverge from the shared auth handling.
export const nextClient = axios.create({
  baseURL: productApiOrigin,
  timeout: 8000,
});

setupAxiosProgramInterceptor(nextClient);

// A backend access_token refresher registered from a React layer (which has
// useAuth0().getAccessTokenSilently). Returns a fresh token, or null when a
// silent re-mint is impossible (no live Auth0 SSO session). This lets the v2
// client recover a momentarily-expired token instead of dead-ending the user
// with "session expired". Registered in NextApp; cleared on sign-out.
type TokenRefresher = () => Promise<string | null>;
let _refreshToken: TokenRefresher | null = null;
export function registerTokenRefresher(fn: TokenRefresher | null): void { _refreshToken = fn; }

// Single-flight: the loaders fan out (Promise.all), so several requests can 401
// at once — coalesce them onto ONE refresh instead of N parallel re-mints.
let _refreshInFlight: Promise<string | null> | null = null;
function refreshAccessTokenOnce(): Promise<string | null> {
  if (!_refreshInFlight) {
    _refreshInFlight = (_refreshToken ? _refreshToken() : Promise.resolve(null))
      .catch(() => null)
      .finally(() => { _refreshInFlight = null; });
  }
  return _refreshInFlight;
}

nextClient.interceptors.request.use((config) => {
  assertOwnedBackendRequest(config.url, config.baseURL, productApiOrigin);
  if (typeof window !== 'undefined' && window.localStorage) {
    const token = window.localStorage.getItem('access_token');
    if (token) {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// On a 401, RECOVER before giving up: a 401 with a token is often just a
// momentarily-expired access_token while the Auth0 SSO session is still alive.
// Try one silent re-mint (registered refresher) and retry the request once. Only
// if that's impossible/fails do we clear the dead token so the app falls back to
// its honest anonymous/sign-in state (we deliberately do NOT redirect to /login,
// so anonymous /next/* viewers aren't yanked out). 401s still bubble to callers.
nextClient.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const cfg = error?.config;
    const is401 = error?.response?.status === 401;
    const sentToken = !!cfg?.headers?.Authorization;
    const canStore = typeof window !== 'undefined' && !!window.localStorage;
    // Recover-first: one silent re-mint + retry. `_retry` guards against a loop
    // if the refreshed token also 401s.
    if (is401 && sentToken && cfg && !(cfg as any)._retry && _refreshToken) {
      (cfg as any)._retry = true;
      const fresh = await refreshAccessTokenOnce();
      if (fresh) {
        if (canStore) window.localStorage.setItem('access_token', fresh);
        cfg.headers = cfg.headers ?? {};
        (cfg.headers as Record<string, string>).Authorization = `Bearer ${fresh}`;
        return nextClient(cfg);
      }
    }
    // Terminal: refresh unavailable/failed, or the retry still 401'd → the token
    // is truly dead. Clear storage and notify the React auth owner.
    if (is401 && sentToken && canStore) {
      const authorization = String(cfg?.headers?.Authorization ?? '');
      const rejectedToken = authorization.startsWith('Bearer ')
        ? authorization.slice('Bearer '.length)
        : null;
      // Several live loaders can fail together. The first terminal response owns
      // expiry and clears the credential; a late response for that same rejected
      // token must not overwrite its reviewed return path (or expire a newly
      // authenticated session that has already installed a different token).
      if (rejectedToken) expireProductSession(undefined, rejectedToken);
    }
    return Promise.reject(error);
  },
);

// Test seam: the configured axios instance (with the real request/response
// interceptors) so unit tests can drive the silent-refresh+retry path through a
// custom adapter instead of the vi.fn()-mocked interceptors.
export const __nextClientForTests = nextClient;

// ── backend response shapes ────────────────────────────────────────────────

interface ApiSummary {
  overall_mastery: number;
  overall_confidence: number;
  strongest_domain: string | null;
  weakest_domain: string | null;
  active_pitfalls_count: number;
  review_due_count: number;
  top_recommendation: unknown;
}

interface ApiMapNode {
  node_id: string;
  label: string;
  node_type: string;
  overlay: {
    mastery_score: number;
    confidence: number;
    mastery_band: string;
    is_blocked: boolean;
    active_pitfall: boolean;
    review_due: boolean;
    recently_improved: boolean;
    priority_score: number;
  };
}

interface ApiRecommendation {
  node_id: string;
  label: string;
  action: string;
  priority_score: number;
  rank: number;
  candidateCount?: number;
  hasPracticePool?: boolean;
  why: {
    why_now: string;
    mastery_signal: string;
    pitfalls: string[];
    strategies: string[];
    blockers: string[];
    evidence_node_ids: string[];
    rationale_snippets: string[];
  };
}

export interface ScoreEstimate {
  scoreEstimateId: string;
  program: 'sat';
  scope: 'run' | 'user' | 'section' | 'skill' | 'micro_skill' | 'recommendation';
  scopeId: string;
  section: 'rw' | 'math' | null;
  unit: 'sat_points' | 'section_points' | 'score_delta' | 'probability' | 'mastery_band';
  value: number | null;
  low: number | null;
  high: number | null;
  source: 'official_export' | 'official_table' | 'answer_ledger' | 'mastery_state' | 'hybrid';
  basis: string;
  modelVersion: string;
  contentVersion: string;
  taxonomyVersion: string;
  difficultyVersion: string;
  confidence: 'official' | 'high' | 'medium' | 'low' | 'insufficient';
  evidence: Record<string, unknown>;
  asOf: string;
  limitations: string[];
}

function optionalNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function legacyNumber(v: unknown, fallback = 0): number {
  const n = optionalNumber(v);
  return n == null ? fallback : n;
}

export function adaptScoreEstimate(raw: any): ScoreEstimate | null {
  const r = raw?.score_estimate ?? raw?.scoreEstimate ?? raw;
  if (!r || typeof r !== 'object') return null;
  return {
    scoreEstimateId: String(r.score_estimate_id ?? r.scoreEstimateId ?? ''),
    program: (r.program ?? 'sat') as ScoreEstimate['program'],
    scope: (r.scope ?? 'user') as ScoreEstimate['scope'],
    scopeId: String(r.scope_id ?? r.scopeId ?? ''),
    section: (r.section ?? null) as ScoreEstimate['section'],
    unit: (r.unit ?? 'sat_points') as ScoreEstimate['unit'],
    value: optionalNumber(r.value),
    low: optionalNumber(r.low),
    high: optionalNumber(r.high),
    source: (r.source ?? 'mastery_state') as ScoreEstimate['source'],
    basis: r.basis ?? '',
    modelVersion: r.model_version ?? r.modelVersion ?? '',
    contentVersion: r.content_version ?? r.contentVersion ?? '',
    taxonomyVersion: r.taxonomy_version ?? r.taxonomyVersion ?? '',
    difficultyVersion: r.difficulty_version ?? r.difficultyVersion ?? '',
    confidence: (r.confidence ?? 'insufficient') as ScoreEstimate['confidence'],
    evidence: r.evidence ?? {},
    asOf: r.as_of ?? r.asOf ?? '',
    limitations: Array.isArray(r.limitations) ? r.limitations : [],
  };
}

function estimateValueOrLegacy(
  estimate: ScoreEstimate | null,
  legacy: unknown,
  fallback: number | null = 0,
): number | null {
  if (estimate) return estimate.value;
  const n = optionalNumber(legacy);
  return n == null ? fallback : n;
}

export interface LibraryPitfallGroup {
  pitfallLabel: string;
  microSkillLabel: string;
  /** The micro-skill UUID the pitfall is attached to. Used to launch a
   *  focused practice session: /next/app/practice/session?focus=<id>&pitfall=<pitfallId>. */
  relatedMicroSkillNodeId: string | null;
  frequency: number;
  activeCount: number;
  resolvedCount: number;
  severity: number;
  description?: string | null;
  howToAvoid?: string | null;
}

export interface LibraryItems {
  counts: {
    mistakes: number;
    flashcards: number;
    savedQuestions: number;
    fullTests: number;
    strategies: number;
    reviewedPitfalls: number;
  };
  pitfallGroups: LibraryPitfallGroup[];
}

export async function loadLibraryItems(): Promise<LibraryItems | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get('/api/sat/library/items');
    const d = res.data?.data ?? {};
    const groups: LibraryPitfallGroup[] = (d.pitfall_groups ?? []).map((g: any) => ({
      pitfallLabel:             g.pitfall_label ?? '',
      microSkillLabel:          g.micro_skill_label ?? '',
      relatedMicroSkillNodeId:  g.related_micro_skill_node_id ?? null,
      frequency:                g.frequency ?? 0,
      activeCount:              g.active_count ?? 0,
      resolvedCount:            g.resolved_count ?? 0,
      severity:                 g.severity ?? 0,
      description:              g.description ?? null,
      howToAvoid:               g.how_to_avoid ?? null,
    }));
    const c = d.counts ?? {};
    return {
      counts: {
        mistakes:          c.mistakes          ?? 0,
        flashcards:        c.flashcards        ?? 0,
        savedQuestions:    c.saved_questions   ?? 0,
        fullTests:         c.full_tests        ?? 0,
        strategies:        c.strategies        ?? 0,
        reviewedPitfalls:  c.reviewed_pitfalls ?? 0,
      },
      pitfallGroups: groups,
    };
  } catch {
    return null;
  }
}

export interface AuthedUser {
  id: string | number;
  email: string | null;
  name: string | null;
  picture: string | null;
}

export async function loadAuthedUser(): Promise<AuthedUser | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get('/api/users/me');
    return {
      id:      res.data?.id      ?? null,
      email:   res.data?.email   ?? null,
      name:    res.data?.name    ?? null,
      picture: res.data?.picture ?? null,
    };
  } catch { return null; }
}

export interface MasteryTrendPoint {
  weekStart: string;
  attempts: number;
  correct: number;
  accuracy: number;
}

export async function loadMasteryTrend(weeks = 8): Promise<MasteryTrendPoint[] | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get('/api/sat/user-kg/mastery-trend', { params: { weeks } });
    return (res.data?.data?.points ?? []).map((p: any) => ({
      weekStart: p.week_start,
      attempts:  p.attempts ?? 0,
      correct:   p.correct  ?? 0,
      accuracy:  p.accuracy ?? 0,
    }));
  } catch { return null; }
}

export interface RecentSession {
  startedAt: string | null;
  itemCount: number;
  correctCount: number;
  topSkill: string;
  accuracy: number;
  /** Server-built session delta ("+N score" for full exams, "C / N" for
   *  plain practice — see recent_sessions.py). The Practice "recent
   *  sessions" rows surface this verbatim. */
  delta: string | null;
  practiceKind?: 'standard' | 'focused' | 'custom' | 'mission' | 'diagnostic' | null;
  practiceInstanceId?: string | null;
}

export async function loadRecentSessions(limit = 10): Promise<RecentSession[] | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get('/api/sat/practice/recent-sessions', { params: { limit } });
    const items = res.data?.data?.items ?? [];
    return items.map((r: any) => ({
      startedAt:    r.started_at ?? null,
      itemCount:    r.item_count ?? 0,
      correctCount: r.correct_count ?? 0,
      topSkill:     r.top_skill ?? 'Practice session',
      accuracy:     r.accuracy ?? 0,
      delta:        r.delta ?? null,
      practiceKind: r.practice_kind ?? null,
      practiceInstanceId: r.practice_instance_id ?? null,
    }));
  } catch { return null; }
}

export interface OwnedPracticeInstance {
  practiceInstanceId: string;
  programId: string;
  practiceKind: 'standard' | 'focused' | 'custom' | 'mission' | 'diagnostic';
  status: string;
  contract: Record<string, unknown>;
}

/** Owner-scoped Task4 resolver. A foreign/missing/malformed instance is null. */
export async function loadPracticeInstance(
  practiceInstanceId: string,
): Promise<OwnedPracticeInstance | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get(
      `/api/sat/practice/instances/${encodeURIComponent(practiceInstanceId)}`,
    );
    const value = res.data?.data;
    const ordinaryKinds = ['standard', 'focused', 'custom', 'mission', 'diagnostic'];
    if (!value || value.practiceInstanceId !== practiceInstanceId
      || value.programId !== 'sat' || !ordinaryKinds.includes(value.practiceKind)
      || typeof value.status !== 'string' || !value.contract) return null;
    return value as OwnedPracticeInstance;
  } catch {
    return null;
  }
}

// ── Practice page (Tier 2 + Tier 4 endpoints) ─────────────────────────────

export interface ComponentStat {
  id: string;             // 'vocab' | 'reading' | 'writing' | 'clues' | 'alg' | ...
  title: string;
  subtitle: string;
  skills: number;         // node count under the component
  coverage: number;       // 0..1
  lastAcc: number | null; // 0..1, last 30d
  band: 'mastered' | 'proficient' | 'developing' | 'weak' | 'unseen';
}

export async function loadComponentStats(section: 'rw' | 'math'): Promise<ComponentStat[] | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get('/api/sat/practice/components', { params: { section } });
    const items = res.data?.data?.components ?? [];
    return items.map((c: any) => ({
      id:        c.id,
      title:     c.title,
      subtitle:  c.subtitle ?? '',
      skills:    c.skills ?? 0,
      coverage:  c.coverage ?? 0,
      lastAcc:   c.last_acc ?? null,
      band:      c.band ?? 'unseen',
    }));
  } catch { return null; }
}

export interface ModuleLastAttempt {
  qs: number;
  mins: number;
  lastAcc: number | null;
  weakest: string;
  attempted: boolean;
}

export async function loadModuleLastAttempt(section: 'rw' | 'math'): Promise<ModuleLastAttempt | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get('/api/sat/practice/modules/last-attempt', { params: { section } });
    const d = res.data?.data ?? {};
    // qs/mins are fixed module-structure constants — the backend doesn't
    // return them (the L2 card just needs DSAT module size for copy).
    return {
      qs:        section === 'rw' ? 27 : 22,
      mins:      section === 'rw' ? 32 : 35,
      lastAcc:   d.last_acc ?? null,
      weakest:   d.weakest ?? '—',
      attempted: !!d.attempted,
    };
  } catch { return null; }
}

export interface FullExamSummary {
  examId: string;
  title: string;
  source: string;
  totalQuestions: number;
  totalTimeS: number;
  modules: number;
}
/** @deprecated use FullExamSummary */
export type DsatExamSummary = FullExamSummary;

/**
 * List structured (4-module Bluebook) exams. Generic over `sources` — the
 * exam catalog groups CB practice vs Mock tests by `source`. Hits the generic
 * /api/sat/exams/full endpoint (?source=<csv>; omit = all sources).
 */
export async function loadFullExams(sources?: string[], limit = 20): Promise<FullExamSummary[] | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const params: Record<string, unknown> = { limit };
    if (sources && sources.length) params.source = sources.join(',');
    const res = await nextClient.get('/api/sat/exams/full', { params });
    const exams = res.data?.data?.exams ?? [];
    return exams.map((e: any) => ({
      examId:         e.exam_id,
      title:          e.title ?? 'Practice Exam',
      source:         e.source ?? 'college_board',
      totalQuestions: e.total_questions ?? 0,
      totalTimeS:     e.total_time_s ?? 0,
      modules:        (e.modules ?? []).length || 4,
    }));
  } catch { return null; }
}
/** @deprecated use loadFullExams */
export const loadDsatExams = (limit = 20) => loadFullExams(undefined, limit);

// ── Full-exam runner (start / module / submit / finish) ────────────────────
// Generic structured-exam endpoints under /api/sat/exams/full/*. These power
// the v2 Bluebook-style runner. Each loader unwraps the `{data}` envelope and
// returns null on any error (the runner surfaces an inline error state).

export interface ExamRunModule {
  moduleId: string;
  section: string;       // 'rw' | 'math'
  moduleIdx: number;     // 1-based within section
  itemCount: number;
  timeLimitS: number;
}

export interface ExamStartResult {
  runId: string;
  modules: ExamRunModule[];
}

export interface ExamLaunchContext {
  practiceLaunchId?: string | null;
  entryOrigin?: string | null;
}

function withExamLaunchContext<T extends Record<string, unknown>>(
  body: T,
  launch?: ExamLaunchContext,
): Record<string, unknown> {
  if (!launch) return body;
  const next: Record<string, unknown> = { ...body };
  if (launch.practiceLaunchId != null) next['practiceLaunchId'] = launch.practiceLaunchId;
  if (launch.entryOrigin != null) next['entryOrigin'] = launch.entryOrigin;
  return next;
}

function toRunModule(m: any): ExamRunModule {
  return {
    moduleId:   m.module_id,
    section:    m.section ?? 'rw',
    moduleIdx:  m.module_idx ?? 1,
    itemCount:  m.item_count ?? 0,
    timeLimitS: m.time_limit_s ?? 0,
  };
}

export async function startFullExam(
  examId: string,
  launch?: ExamLaunchContext,
): Promise<ExamStartResult | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.post(
      `/api/sat/exams/full/${examId}/start`,
      withExamLaunchContext({}, launch),
    );
    const d = res.data?.data ?? {};
    return {
      runId:   d.run_id,
      modules: (d.modules ?? []).map(toRunModule),
    };
  } catch { return null; }
}

export interface ExamItem {
  itemContentId: string;
  orderIndex: number;
  questionType: string;          // 'mcq_single' | 'short_text' | ...
  stem: string;
  choices: ExamChoice[];
  answerKey: AnswerKey | null;
  metadata: any;
}

export interface AnswerKey {
  type?: string;
  label?: string;
  correct_label?: string;
  accepted?: unknown;
  cb_dsat_spr_v1?: {
    version?: string;
    accepted_entries?: unknown;
    display_entries?: unknown;
    rules?: Record<string, unknown>;
  };
  [key: string]: unknown;
}

export interface ExamChoiceFigure {
  url?: string | null;
  figure_type?: string | null;
  alt_text?: string | null;
  long_description?: string | null;
}

export interface ExamChoice {
  label: string;
  text: string;
  figure?: ExamChoiceFigure;
}

export interface ExamModule {
  moduleId: string;
  section: string;
  moduleIdx: number;
  timeLimitS: number;
  items: ExamItem[];
}

// ── shared resilience helpers (Phase 3) ────────────────────────────────────
function axiosStatus(err: any): number | undefined {
  return err?.response?.status;
}
// Retry on network/timeout (no response) or 5xx/429; never on other 4xx.
function isTransient(err: any): boolean {
  const s = axiosStatus(err);
  if (s == null) return true;
  return s >= 500 || s === 429;
}
async function withRetry<T>(fn: () => Promise<T>, retries = 2): Promise<T> {
  let lastErr: any;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try { return await fn(); }
    catch (err) {
      lastErr = err;
      if (attempt === retries || !isTransient(err)) break;
      await new Promise((r) => setTimeout(r, 250 * (attempt + 1))); // 250ms, 500ms
    }
  }
  throw lastErr;
}

// ── exam-module content cache (Phase 2) ─────────────────────────────────────
// Mirrors v1's React Query staleTime: avoid refetching module content on
// module switch / resume / jump-back within a session. In-memory (per page
// load), keyed by examId:moduleId.
const MODULE_CACHE_TTL_MS = 10 * 60 * 1000;
const _moduleCache = new Map<string, { at: number; mod: ExamModule }>();
export function clearExamModuleCache(): void { _moduleCache.clear(); }

export async function loadExamModule(examId: string, moduleId: string): Promise<ExamModule | null> {
  if (!hasRealAuthToken()) return null;
  const cacheKey = `${examId}:${moduleId}`;
  const cached = _moduleCache.get(cacheKey);
  if (cached && Date.now() - cached.at < MODULE_CACHE_TTL_MS) return cached.mod;
  try {
    const res = await withRetry(() => nextClient.get(`/api/sat/exams/full/${examId}/module/${moduleId}`));
    const d = res.data?.data ?? {};
    const items: ExamItem[] = (d.items ?? []).map((it: any) => {
      const rawChoices = it.choices ?? [];
      const choices = Array.isArray(rawChoices)
        ? rawChoices.map((c: any) => ({
            label: String(c.label ?? c.id ?? '').toUpperCase(),
            text:  c.text ?? c.value ?? '',
            figure: c.figure && typeof c.figure === 'object' ? {
              url: c.figure.url ?? null,
              figure_type: c.figure.figure_type ?? null,
              alt_text: c.figure.alt_text ?? null,
              long_description: c.figure.long_description ?? null,
            } : undefined,
          }))
        : [];
      return {
        itemContentId: it.item_content_id,
        orderIndex:    it.order_index ?? 0,
        questionType:  it.question_type ?? 'mcq_single',
        stem:          it.stem ?? '',
        choices,
        answerKey:     it.answer_key ?? it.answer_key_json ?? null,
        metadata:      it.metadata_json ?? it.metadata ?? null,
      };
    });
    const mod: ExamModule = {
      moduleId:   d.module_id ?? moduleId,
      section:    d.section ?? 'rw',
      moduleIdx:  d.module_idx ?? 1,
      timeLimitS: d.time_limit_s ?? 0,
      items,
    };
    _moduleCache.set(cacheKey, { at: Date.now(), mod });
    return mod;
  } catch { return null; }
}

export interface ExamAnswer {
  itemContentId: string;
  selectedOption?: string | null;  // MCQ: 'A'..'D'
  value?: string | null;           // grid-in / SPR: typed string
  timeSpentMs?: number;
}

export interface ModuleSubmitResult {
  correct: number;
  total: number;
  moduleScore: number | null;
  nextModuleId: string | null;
}

// Phase 3: a structured outcome so the runner can react to *why* a submit
// failed — terminal (run gone/finished/bad data) vs. recoverable (network),
// where the saved snapshot lets the user resume + retry.
export type SubmitFailureKind = 'unauthenticated' | 'conflict' | 'notfound' | 'invalid' | 'network';
export type SubmitOutcome =
  | { ok: true; result: ModuleSubmitResult }
  | { ok: false; kind: SubmitFailureKind };

// Autosave one in-progress answer so it lands in question_attempts (and thus
// Activity/history) before the module is submitted. Fire-and-forget: errors are
// swallowed and mock-token/anon sessions skip the network entirely.
export async function saveExamAnswer(
  runId: string, moduleId: string, answer: ExamAnswer, launch?: ExamLaunchContext,
): Promise<void> {
  if (!hasRealAuthToken()) return;
  if (!answer.itemContentId) return;
  const out: Record<string, unknown> = { item_content_id: answer.itemContentId };
  if (answer.selectedOption != null) out.selected_option = answer.selectedOption;
  if (answer.value != null)          out.value = answer.value;
  if (answer.timeSpentMs != null)    out.time_spent_ms = answer.timeSpentMs;
  try {
    await nextClient.post(
      `/api/sat/exams/full/runs/${runId}/module/${moduleId}/answer`,
      withExamLaunchContext(out, launch),
    );
  } catch { /* fire-and-forget */ }
}

// Safety-net batch flush of all in-progress answers for a module. Re-pushes the
// full answered set; the server upsert is idempotent (deterministic
// client_attempt_uuid), so a previously-dropped per-answer write self-heals
// here. Used by the runner's interval / beforeunload / resume sync. `keepalive`
// routes through fetch so the request survives a page unload (axios can't).
export async function flushExamAnswers(
  runId: string, moduleId: string, answers: ExamAnswer[], opts: { keepalive?: boolean } = {},
  launch?: ExamLaunchContext,
): Promise<boolean> {
  if (!hasRealAuthToken()) return false;
  const list = answers.filter((a) => a.itemContentId && (a.selectedOption != null || (a.value != null && a.value !== '')));
  if (!list.length) return true;
  const payload = withExamLaunchContext({
    answers: list.map((a) => {
      const out: Record<string, unknown> = { item_content_id: a.itemContentId };
      if (a.selectedOption != null) out.selected_option = a.selectedOption;
      if (a.value != null)          out.value = a.value;
      if (a.timeSpentMs != null)    out.time_spent_ms = a.timeSpentMs;
      return out;
    }),
  }, launch);
  const url = `/api/sat/exams/full/runs/${runId}/module/${moduleId}/answers`;
  try {
    if (opts.keepalive && typeof fetch === 'function') {
      const token = typeof window !== 'undefined' ? window.localStorage.getItem('access_token') : null;
      // Raw fetch bypasses the axios interceptor, so the owned-origin authority
      // must be applied explicitly. A bare relative URL resolves against the
      // PAGE origin, which is the Product API origin only in development
      // (where it is deliberately empty so Vite can proxy) — in production it
      // silently posted the unload flush to the frontend host instead.
      const keepaliveUrl = resolveBackendUrl(url, productApiOrigin);
      assertOwnedBackendRequest(keepaliveUrl, productApiOrigin, productApiOrigin);
      const res = await fetch(keepaliveUrl, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });
      // Report the real outcome: a 401/422/5xx is a lost flush, not a save.
      return res.ok;
    }
    await nextClient.post(url, payload);
    return true;
  } catch { return false; }
}

export async function submitExamModule(
  runId: string, moduleId: string, answers: ExamAnswer[], launch?: ExamLaunchContext,
): Promise<SubmitOutcome> {
  if (!hasRealAuthToken()) return { ok: false, kind: 'unauthenticated' };
  const body = withExamLaunchContext({
    answers: answers.map((a) => {
      const out: Record<string, unknown> = { item_content_id: a.itemContentId };
      if (a.selectedOption != null) out.selected_option = a.selectedOption;
      if (a.value != null)          out.value = a.value;
      if (a.timeSpentMs != null)    out.time_spent_ms = a.timeSpentMs;
      return out;
    }),
  }, launch);
  try {
    const res = await withRetry(() => nextClient.post(
      `/api/sat/exams/full/runs/${runId}/module/${moduleId}/submit`, body,
    ));
    const d = res.data?.data ?? {};
    return {
      ok: true,
      result: {
        correct:      d.correct ?? 0,
        total:        d.total ?? 0,
        moduleScore:  d.module_score ?? null,
        nextModuleId: d.next_module_id ?? null,
      },
    };
  } catch (err) {
    const s = axiosStatus(err);
    const kind: SubmitFailureKind =
      s === 409 ? 'conflict'
      : s === 404 ? 'notfound'
      : s === 422 ? 'invalid'
      : (s === 401 || s === 403) ? 'unauthenticated'
      : 'network';
    return { ok: false, kind };
  }
}

export interface ScoreBand { lower: number; upper: number; }

export interface ExamFinishResult {
  totalScore: number | null;
  rwScore: number | null;
  mathScore: number | null;
  rwRaw: number;
  mathRaw: number;
  scoreBands: { rw: ScoreBand; math: ScoreBand; total: ScoreBand };
  scoringMethod: string | null;
  scoreEstimate?: ScoreEstimate | null;
  rwScoreEstimate?: ScoreEstimate | null;
  mathScoreEstimate?: ScoreEstimate | null;
}

export async function finishFullExam(runId: string): Promise<ExamFinishResult | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await withRetry(() => nextClient.post(`/api/sat/exams/full/runs/${runId}/finish`));
    const d = res.data?.data ?? {};
    const band = (b: any): ScoreBand => ({ lower: b?.lower ?? 0, upper: b?.upper ?? 0 });
    const bands = d.score_bands ?? {};
    const scoreEstimate = adaptScoreEstimate(d.score_estimate ?? d.scoreEstimate);
    const rwScoreEstimate = adaptScoreEstimate(d.rw_score_estimate ?? d.rwScoreEstimate);
    const mathScoreEstimate = adaptScoreEstimate(d.math_score_estimate ?? d.mathScoreEstimate);
    return {
      totalScore:    estimateValueOrLegacy(scoreEstimate, d.total_score ?? d.totalScore, null),
      rwScore:       estimateValueOrLegacy(rwScoreEstimate, d.rw_score ?? d.rwScore, null),
      mathScore:     estimateValueOrLegacy(mathScoreEstimate, d.math_score ?? d.mathScore, null),
      rwRaw:         d.rw_raw ?? 0,
      mathRaw:       d.math_raw ?? 0,
      scoreBands: {
        rw:    band(bands.rw),
        math:  band(bands.math),
        total: band(bands.total),
      },
      scoringMethod: d.scoring_method ?? d.scoringMethod ?? (scoreEstimate ? 'score_estimator_v0' : null),
      scoreEstimate,
      rwScoreEstimate,
      mathScoreEstimate,
    };
  } catch { return null; }
}

// Phase 0 / PR 488: run-state lookup for resume validation.
export interface ExamLiveState {
  phase: 'intro' | 'question' | 'review' | string;
  moduleId: string;
  moduleIdx: number;
  qIdx: number;
  timerByModule: Record<string, number>;
  paused: boolean;
  updatedAt: number;
}

export interface ExamRunAnswerState { selected?: string; value?: string; flagged?: boolean; }

export interface ExamRunState {
  runId: string;
  examId: string;
  examTitle?: string;
  status: 'in_progress' | 'finished';
  submittedModules: string[];
  liveState: ExamLiveState | null;
  answersByModule: Record<string, Record<string, ExamRunAnswerState>>;
  modules: ExamRunModule[];
  startedAt: string | null;
  completedAt: string | null;
}

function mapLiveState(raw: any): ExamLiveState | null {
  if (!raw) return null;
  return {
    phase: raw.phase ?? 'question',
    moduleId: raw.module_id ?? raw.moduleId ?? '',
    moduleIdx: raw.module_idx ?? raw.moduleIdx ?? 0,
    qIdx: raw.q_idx ?? raw.qIdx ?? 0,
    timerByModule: raw.timer_by_module ?? raw.timerByModule ?? {},
    paused: !!raw.paused,
    updatedAt: raw.updated_at ?? raw.updatedAt ?? 0,
  };
}

function mapAnswersByModule(raw: any): Record<string, Record<string, ExamRunAnswerState>> {
  const out: Record<string, Record<string, ExamRunAnswerState>> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [moduleId, items] of Object.entries(raw)) {
    if (!items || typeof items !== 'object') continue;
    out[moduleId] = {};
    for (const [itemId, ans] of Object.entries(items as Record<string, any>)) {
      const entered = ans?.entered_answer ?? ans?.enteredAnswer;
      const selected = ans?.selected_option ?? ans?.selectedOption;
      out[moduleId][itemId] = entered != null && entered !== ''
        ? { value: String(entered) }
        : selected != null ? { selected: String(selected) } : {};
    }
  }
  return out;
}

function mapExamRunState(d: any, fallbackRunId = ''): ExamRunState {
  return {
    runId:            d.run_id ?? fallbackRunId,
    examId:           d.exam_id ?? '',
    examTitle:        d.exam_title ?? d.examTitle,
    status:           d.status === 'finished' ? 'finished' : 'in_progress',
    submittedModules: Array.isArray(d.submitted_modules) ? d.submitted_modules : [],
    liveState:        mapLiveState(d.live_state ?? d.liveState),
    answersByModule:  mapAnswersByModule(d.answers_by_module ?? d.answersByModule),
    modules:          Array.isArray(d.modules) ? d.modules.map(toRunModule) : [],
    startedAt:        d.started_at ?? null,
    completedAt:      d.completed_at ?? null,
  };
}

// Returns the run state, 'gone' (404/403 — no longer resumable), or null
// (network/unauth — can't tell; caller should resume optimistically/offline).
export async function loadExamRun(runId: string): Promise<ExamRunState | 'gone' | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get(`/api/sat/exams/full/runs/${runId}`);
    return mapExamRunState(res.data?.data ?? {}, runId);
  } catch (err) {
    const s = axiosStatus(err);
    if (s === 404 || s === 403) return 'gone';
    return null; // network/unknown → don't block resume
  }
}

export async function findActiveExamRun(examId: string): Promise<ExamRunState | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get('/api/sat/exams/full/runs/active', { params: { exam_id: examId } });
    const d = res.data?.data;
    return d ? mapExamRunState(d) : null;
  } catch {
    return null;
  }
}

export async function saveExamLiveState(
  runId: string,
  state: ExamLiveState,
  opts: { keepalive?: boolean } = {},
): Promise<boolean> {
  if (!hasRealAuthToken()) return false;
  const payload = {
    phase: state.phase,
    module_id: state.moduleId,
    module_idx: state.moduleIdx,
    q_idx: state.qIdx,
    timer_by_module: state.timerByModule,
    paused: state.paused,
    updated_at: state.updatedAt,
  };
  const url = `/api/sat/exams/full/runs/${runId}/state`;
  try {
    if (opts.keepalive && typeof fetch === 'function') {
      const token = typeof window !== 'undefined' ? window.localStorage.getItem('access_token') : null;
      // Same origin authority as the answer flush: a bare relative URL resolves
      // against the PAGE origin, which is the Product API only in development.
      // This request carries the resume position and timer, so in production the
      // student's place in the exam never reached the server on unload.
      const keepaliveStateUrl = resolveBackendUrl(url, productApiOrigin);
      assertOwnedBackendRequest(keepaliveStateUrl, productApiOrigin, productApiOrigin);
      const res = await fetch(keepaliveStateUrl, {
        method: 'PUT',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });
      return res.ok;
    }
    const res = await nextClient.put(url, payload);
    return !!res.data?.data?.saved;
  } catch {
    return false;
  }
}

// ── Review-flow loaders (Test Review / Activity / Progress) ─────────────────
// Consume the read endpoints in routes/sat/dsat_exam.py + practice_components.py.
// UI pending designs; these are the typed data hooks the screens will call.

// Per-item taxonomy tag — drives the skill chip + the "Practice this skill"
// deep-link (code → practice/session?types=<code>&section=…).
export interface SkillTag { nodeId?: string | null; code: string; label: string; domain: string | null; }
export interface ReviewAnswer {
  selectedOption: string | null;
  enteredAnswer: string | null;
  answerType: string | null;
  isCorrect: boolean;
  timeSpentMs: number | null;
}
export interface TaxonomyNodeRef { nodeId: string | null; code: string | null; label: string | null; }
export interface TaxonomyLineage { domain: TaxonomyNodeRef | null; skill: TaxonomyNodeRef | null; microSkill: TaxonomyNodeRef | null; }
export interface KGConcept { name: string; description?: string | null; coreLabel?: string | null; }
export interface KGPitfall { name: string; description?: string | null; whyItHappens?: string | null; howToAvoid?: string | null; }
export interface KGStrategy { name: string; description?: string | null; whenToUse?: string | null; }
export interface KnowledgeBlock {
  concepts: KGConcept[]; pitfalls: KGPitfall[]; strategies: KGStrategy[];
  coverage: 'full' | 'partial' | 'none';
}
export interface ReviewItem {
  itemContentId: string;
  orderIndex: number;
  questionType: string;
  stem: string;
  choices: any;
  answerKey: AnswerKey | null;
  explanation: string | null;
  metadata: any;
  skill: SkillTag | null;
  pitfall: string | null;            // named pitfall for the wrong-answer callout
  taxonomy?: TaxonomyLineage | null;  // Fn3: domain > skill > micro-skill lineage
  knowledge?: KnowledgeBlock | null;  // Fn3: concepts/pitfalls/strategies + coverage
  yourAnswer: ReviewAnswer | null;   // null if the student didn't answer it
}
export interface ReviewModule {
  moduleId: string; section: string; moduleIdx: number; items: ReviewItem[];
}
export interface ScoreSectionVM { score: number | null; correct: number; total: number; band: string; low: number | null; high: number | null; }
export interface ScoreSummary {
  examTitle: string | null;
  total: number | null; totalLow: number | null; totalHigh: number | null; totalBand: string;
  rw: ScoreSectionVM; math: ScoreSectionVM;
  correct: number; answered: number; timeUsedS: number; takenAt: string | null;
}
export interface ExamReview {
  runId: string; examId: string; status: string;
  totalScore: number | null; rwScore: number | null; mathScore: number | null;
  completedAt: string | null; score: ScoreSummary | null; modules: ReviewModule[];
}

function adaptScoreSection(s: any): ScoreSectionVM {
  return { score: s?.score ?? null, correct: s?.correct ?? 0, total: s?.total ?? 0,
    band: s?.band ?? 'unseen', low: s?.low ?? null, high: s?.high ?? null };
}

/** GET /api/sat/practice/items/{id} — canonical FULL content for one item (stem,
 * choices, answer_key, merged pp_groups stimulus on metadata_json.stimulus, the real
 * explanation column, skill). Used to hydrate the Activity "visit incorrect" sheet,
 * which otherwise renders the partial History shape (no passage/explanation). */
export async function loadItemContent(itemContentId: string, studentKey?: string): Promise<any | null> {
  if (!hasRealAuthToken() || !itemContentId) return null;
  try {
    const path = studentKey
      ? `/api/family/students/${encodeURIComponent(studentKey)}/items/${encodeURIComponent(itemContentId)}`
      : `/api/sat/practice/items/${encodeURIComponent(itemContentId)}`;
    const res = await nextClient.get(path);
    return res.data ?? null;   // endpoint returns the item dict directly
  } catch { return null; }
}

export async function loadTestReview(runId: string): Promise<ExamReview | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get(`/api/sat/exams/full/runs/${runId}/review`);
    const d = res.data?.data ?? {};
    return {
      runId: d.run_id, examId: d.exam_id, status: d.status,
      totalScore: d.total_score ?? null, rwScore: d.rw_score ?? null, mathScore: d.math_score ?? null,
      completedAt: d.completed_at ?? null,
      score: d.score ? {
        examTitle: d.score.exam_title ?? null,
        total: d.score.total ?? null, totalLow: d.score.total_low ?? null, totalHigh: d.score.total_high ?? null,
        totalBand: d.score.total_band ?? 'unseen',
        rw: adaptScoreSection(d.score.rw), math: adaptScoreSection(d.score.math),
        correct: d.score.correct ?? 0, answered: d.score.answered ?? 0,
        timeUsedS: d.score.time_used_s ?? 0, takenAt: d.score.taken_at ?? null,
      } : null,
      modules: (d.modules ?? []).map((m: any) => ({
        moduleId: m.module_id, section: m.section, moduleIdx: m.module_idx,
        items: (m.items ?? []).map((it: any) => ({
          itemContentId: it.item_content_id, orderIndex: it.order_index ?? 0,
          questionType: it.question_type ?? 'mcq_single',
          stem: it.stem ?? '', choices: it.choices ?? [], answerKey: it.answer_key ?? null,
          explanation: it.explanation ?? null, metadata: it.metadata_json ?? {},
          skill: it.skill ? { nodeId: it.skill.nodeId ?? null, code: it.skill.code, label: it.skill.label, domain: it.skill.domain ?? null } : null,
          pitfall: it.pitfall ?? null,
          taxonomy: it.taxonomy ?? null,   // backend already camelCase {domain,skill,microSkill}
          knowledge: it.knowledge ?? null, // {concepts,pitfalls,strategies,coverage}
          yourAnswer: it.your_answer ? {
            selectedOption: it.your_answer.selected_option ?? null,
            enteredAnswer:  it.your_answer.entered_answer ?? null,
            answerType:     it.your_answer.answer_type ?? null,
            isCorrect:      !!it.your_answer.is_correct,
            timeSpentMs:    it.your_answer.time_spent_ms ?? null,
          } : null,
        })),
      })),
    };
  } catch { return null; }
}

// ── Completed-run diagnostic (results "where to focus" + recommendations) ───
export interface DiagnosticWeakArea {
  level: string;
  taxonomyNodeId: string | null;
  code: string | null;
  label: string;
  domain: string | null;
  attempted: number;
  missed: number;
  accuracy: number;
  priority: 'high' | 'medium';
  fallbackUsed: boolean;
  reason: string;
}
export interface DiagnosticRecommendation {
  actionType: string;
  label: string;
  taxonomyNodeId: string | null;
  taxonomyCode: string | null;
  domain: string | null;
  reason: string;
  evidenceCount: number;
  priority: 'high' | 'medium';
  sourceSurface: string;
}
export interface DiagnosisCopy { headline: string; body: string; parentSummary: string; }
export interface RunDiagnostic {
  runId: string; examId: string; status: string;
  score: { total: number | null; readingWriting: number | null; math: number | null; confidence: string };
  diagnosis: DiagnosisCopy | null;
  confidenceCopy: string | null;
  topWeakAreas: DiagnosticWeakArea[];
  recommendations: DiagnosticRecommendation[];
  noEvidenceReason: string | null;
  limitations: string[];
}

// ── DSAT learning-funnel analytics emission ────────────────────────────────
// Fire-and-forget. The server injects userId from the token + validates the
// contract (backend/constants/analytics_events.py); we send camelCase fields.
// `sourceSurface` must be one of results | review | history | insights.
export interface AnalyticsEvent {
  event: string;
  examId?: string | null;
  runId?: string | null;
  sourceSurface?: string | null;
  moduleId?: string | null;
  questionId?: string | null;
  taxonomyNodeId?: string | null;
  domain?: string | null;
  skill?: string | null;
  poolSource?: 'past_exam' | string | null;
  // Insights section_viewed naming (§9a.6 / §9b.6 — backend AnalyticsEventIn
  // accepts these; REQUIRED_FIELDS[SECTION_VIEWED] gates on `sectionId`).
  sectionId?: string | null;
  sectionName?: string | null;
  // Recommendation scope grain (micro_skill | skill) so micro vs skill tab
  // views/clicks are distinguishable in analytics.
  scopeType?: 'micro_skill' | 'skill' | string | null;
  // AI Insights Recommendations (PR A, proposal §7): t3_row_clicked carries the
  // cell (taxonomyNodeId + band) and its rank; t2 events carry the habitId.
  // Backend AnalyticsEventIn must accept these (backend agent contract).
  rank?: number | null;
  band?: string | null;
  habitId?: string | null;
  // T2 carousel redesign (t2_point_selected / t2_question_nav /
  // t2_evidence_completed / t2_practice_clicked — design spec §G). Indices are
  // 0-based. `method` disambiguates tab vs end-link habit selection and
  // button vs swipe vs key slide navigation.
  index?: number | null;
  method?: 'tab' | 'end_link' | 'button' | 'swipe' | 'key' | string | null;
  direction?: 'next' | 'prev' | string | null;
  toIndex?: number | null;
  nQuestions?: number | null;
  target?: 'drill_link' | 'point_set' | string | null;
  // Which AI theme launched the practice session ('ai_t3' today) — carried
  // from the deep_link `recoSource` query param into
  // targeted_practice_started/completed so T3-launched completions are
  // distinguishable from legacy InsPriorities launches (proposal §7 KR).
  recoSource?: string | null;
  /** Server-owned practice instance identity; never derive this from a retry key. */
  practiceInstanceId?: string | null;
  clientEventId?: string | null;   // dedupe key (e.g. one per results mount)
}

/** Privacy-bounded causal launch event. Authenticated user identity and
 * occurrence time are authored by the shared analytics transport/server. */
export interface SurfaceSessionEvent extends AnalyticsEvent {
  event: 'surface_session_checkpoint' | 'surface_session';
  surfaceKey: string;
  visibleSeconds: number;
  startedAt: string;
  endedAt: string;
  checkpointSeq: number;
  clientSessionUuid: string;
  measurementVersion: 1;
}


/** Fire-and-forget for most callers (return value ignored). Returns a
 * Promise<boolean> — true iff the POST was actually dispatched without
 * throwing, false if unauthenticated or the request failed — so callers
 * that need to know whether the emit really went out (e.g. `surfaceViewed`
 * deciding whether to set its dedupe guard) can await it. */
export function logAnalyticsEvent(ev: AnalyticsEvent): Promise<boolean> {
  if (!hasRealAuthToken()) return Promise.resolve(false);
  // Best-effort: never block UI, never surface errors.
  return nextClient.post('/api/sat/analytics/events', {
    ...ev,
    occurredAt: new Date().toISOString(),
  }).then(() => true).catch(() => false);
}

type PracticeLaunchAttemptOutcome =
  | 'success'
  | 'retryable_failure'
  | 'definitive_failure'
  | 'unavailable';

type PracticeLaunchDeliveryOutcome = {
  delivered: boolean;
  cache: boolean;
};

const PRACTICE_LAUNCH_MAX_ATTEMPTS = 3;
const PRACTICE_LAUNCH_RETRY_DELAYS_MS = [250, 750] as const;
const PRACTICE_LAUNCH_ATTEMPT_TIMEOUT_MS = 8000;
const PRACTICE_LAUNCH_COMPLETION_CACHE_MAX = 128;
const RETRYABLE_ANALYTICS_STATUSES = new Set([408, 425, 429]);
const practiceLaunchInFlight = new Map<string, Promise<boolean>>();
const completedPracticeLaunches = new Map<string, boolean>();

function practiceLaunchCoordinatorKey(ev: PracticeLaunchEvent): string {
  return JSON.stringify({
    practiceLaunchId: ev.practiceLaunchId,
    sourceSurface: ev.sourceSurface,
    sourceAction: ev.sourceAction,
    sourceSurfaceSessionId: ev.sourceSurfaceSessionId ?? null,
    targetKind: ev.targetKind,
    practiceMode: ev.practiceMode,
    section: ev.section ?? null,
    taxonomyNodeId: ev.taxonomyNodeId ?? null,
    taxonomyCodes: ev.taxonomyCodes ? [...ev.taxonomyCodes].sort() : null,
    difficultyBands: ev.difficultyBands ? [...ev.difficultyBands].sort() : null,
    examId: ev.examId ?? null,
    runId: ev.runId ?? null,
    habitSessionId: ev.habitSessionId ?? null,
    practiceInstanceId: ev.practiceInstanceId ?? null,
  });
}

function waitForPracticeLaunchRetry(delayMs: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, delayMs));
}

function rememberCompletedPracticeLaunch(coordinatorKey: string, delivered: boolean): void {
  if (completedPracticeLaunches.size >= PRACTICE_LAUNCH_COMPLETION_CACHE_MAX) {
    const oldest = completedPracticeLaunches.keys().next().value;
    if (oldest !== undefined) completedPracticeLaunches.delete(oldest);
  }
  completedPracticeLaunches.set(coordinatorKey, delivered);
}

async function postPracticeLaunchAttempt(body: string): Promise<PracticeLaunchAttemptOutcome> {
  if (!hasRealAuthToken() || typeof window === 'undefined') return 'unavailable';
  const token = window.localStorage.getItem('access_token');
  if (!token) return 'unavailable';

  const controller = typeof AbortController === 'undefined' ? null : new AbortController();
  const timeout = controller
    ? setTimeout(() => controller.abort(), PRACTICE_LAUNCH_ATTEMPT_TIMEOUT_MS)
    : null;
  try {
    const analyticsUrl = resolveBackendUrl('/api/sat/analytics/events', productApiOrigin);
    assertOwnedBackendRequest(analyticsUrl, productApiOrigin, productApiOrigin);
    const response = await fetch(analyticsUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body,
      // The launch envelope is strictly bounded, so it is safe for keepalive.
      // Beacon cannot carry the required Bearer authorization header.
      keepalive: true,
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (response.ok) return 'success';
    if (response.status >= 500 || RETRYABLE_ANALYTICS_STATUSES.has(response.status)) {
      return 'retryable_failure';
    }
    return 'definitive_failure';
  } catch {
    // Fetch rejection includes network, timeout/abort, and other transport failures.
    return 'retryable_failure';
  } finally {
    if (timeout !== null) clearTimeout(timeout);
  }
}

async function deliverPracticeLaunch(ev: PracticeLaunchEvent): Promise<PracticeLaunchDeliveryOutcome> {
  // Serialize once so every retry is the identical idempotent event envelope.
  const body = JSON.stringify({ ...ev, occurredAt: new Date().toISOString() });
  for (let attempt = 0; attempt < PRACTICE_LAUNCH_MAX_ATTEMPTS; attempt += 1) {
    if (attempt > 0) await waitForPracticeLaunchRetry(PRACTICE_LAUNCH_RETRY_DELAYS_MS[attempt - 1]);
    const outcome = await postPracticeLaunchAttempt(body);
    if (outcome === 'success') return { delivered: true, cache: true };
    if (outcome === 'definitive_failure') return { delivered: false, cache: true };
    if (outcome === 'unavailable') return { delivered: false, cache: false };
  }
  return { delivered: false, cache: true };
}

export function logPracticeLaunched(
  ev: PracticeLaunchEvent,
): Promise<boolean> {
  const coordinatorKey = practiceLaunchCoordinatorKey(ev);
  if (completedPracticeLaunches.has(coordinatorKey)) {
    return Promise.resolve(completedPracticeLaunches.get(coordinatorKey) === true);
  }
  const pending = practiceLaunchInFlight.get(coordinatorKey);
  if (pending) return pending;

  const delivery = deliverPracticeLaunch(ev)
    .then(outcome => {
      if (outcome.cache) {
        rememberCompletedPracticeLaunch(coordinatorKey, outcome.delivered);
      }
      return outcome.delivered;
    })
    .catch(() => false)
    .finally(() => {
      if (practiceLaunchInFlight.get(coordinatorKey) === delivery) {
        practiceLaunchInFlight.delete(coordinatorKey);
      }
    });
  practiceLaunchInFlight.set(coordinatorKey, delivery);
  return delivery;
}

/** Authenticated, best-effort surface-session transport. Fetch is used for
 * pagehide because Beacon cannot carry the existing Bearer authorization. */
export async function postSurfaceSessionEvent(
  ev: SurfaceSessionEvent,
  options: { keepalive?: boolean } = {},
): Promise<boolean> {
  if (!hasRealAuthToken() || typeof window === 'undefined') return false;
  const token = window.localStorage.getItem('access_token');
  if (!token) return false;
  try {
    const analyticsUrl = resolveBackendUrl('/api/sat/analytics/events', productApiOrigin);
    assertOwnedBackendRequest(analyticsUrl, productApiOrigin, productApiOrigin);
    const response = await fetch(analyticsUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify(ev),
      keepalive: options.keepalive === true,
    });
    const accepted = response.ok;
    // This fire-and-forget transport never reads the response. Explicitly
    // release its body so an intercepted browser response cannot remain
    // active and hold navigation out of network-idle.
    if (response.body) await response.body.cancel().catch(() => undefined);
    return accepted;
  } catch {
    return false;
  }
}

export async function loadRunDiagnostic(runId: string): Promise<RunDiagnostic | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get(`/api/sat/exams/full/runs/${runId}/diagnostic`);
    const d = res.data?.data ?? {};
    return {
      runId: d.runId, examId: d.examId, status: d.status,
      score: {
        total: d.score?.total ?? null,
        readingWriting: d.score?.readingWriting ?? null,
        math: d.score?.math ?? null,
        confidence: d.score?.confidence ?? 'directional_baseline',
      },
      diagnosis: d.diagnosis ? {
        headline: d.diagnosis.headline ?? '', body: d.diagnosis.body ?? '',
        parentSummary: d.diagnosis.parentSummary ?? '',
      } : null,
      confidenceCopy: d.confidenceCopy ?? null,
      topWeakAreas: (d.topWeakAreas ?? []).map((w: any) => ({
        level: w.level, taxonomyNodeId: w.taxonomyNodeId ?? null, code: w.code ?? null,
        label: w.label, domain: w.domain ?? null,
        attempted: w.attempted ?? 0, missed: w.missed ?? 0, accuracy: w.accuracy ?? 0,
        priority: w.priority ?? 'medium', fallbackUsed: !!w.fallbackUsed, reason: w.reason ?? '',
      })),
      recommendations: (d.recommendations ?? []).map((r: any) => ({
        actionType: r.actionType, label: r.label, taxonomyNodeId: r.taxonomyNodeId ?? null,
        taxonomyCode: r.taxonomyCode ?? null,
        domain: r.domain ?? null, reason: r.reason ?? '', evidenceCount: r.evidenceCount ?? 0,
        priority: r.priority ?? 'medium', sourceSurface: r.sourceSurface ?? 'results',
      })),
      noEvidenceReason: d.noEvidenceReason ?? null,
      limitations: d.limitations ?? [],
    };
  } catch { return null; }
}

export interface HistoryItem {
  attemptId: number;
  timestamp: number | null;
  timeSpentMs: number | null;
  isCorrect: boolean;
  selectedOption: string | null;
  enteredAnswer: string | null;
  answerType: string | null;
  source: string | null;
  mode: string | null;
  testRunId: string | null;   // present → opens the Test Review for that run
  itemContentId: string;
  stem: string | null;
  choices: any;
  answerKey: AnswerKey | null;
  skill: SkillTag | null;
}
export interface HistoryFilters {
  limit?: number; offset?: number;
  source?: 'all' | 'practice' | 'exam';
  correctness?: 'all' | 'correct' | 'incorrect';
  section?: 'rw' | 'math';
}
export async function loadHistory(opts: HistoryFilters = {}): Promise<HistoryItem[] | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get('/api/sat/practice/history', {
      params: {
        limit: opts.limit ?? 20, offset: opts.offset ?? 0,
        source: opts.source ?? 'all', correctness: opts.correctness ?? 'all',
        ...(opts.section ? { section: opts.section } : {}),
      },
    });
    const items = res.data?.data?.items ?? [];
    return items.map((r: any) => ({
      attemptId: r.attempt_id, timestamp: r.timestamp ?? null, timeSpentMs: r.time_spent_ms ?? null,
      isCorrect: !!r.is_correct,
      selectedOption: r.selected_option ?? null, enteredAnswer: r.entered_answer ?? null,
      answerType: r.answer_type ?? null, source: r.source ?? null, mode: r.mode ?? null,
      testRunId: r.test_run_id ?? null, itemContentId: r.item_content_id,
      stem: r.stem ?? null, choices: r.choices ?? [], answerKey: r.answer_key ?? null,
      skill: r.skill ? { code: r.skill.code, label: r.skill.label, domain: r.skill.domain ?? null } : null,
    }));
  } catch { return null; }
}

export interface ProgressCategory {
  nodeId: string; code: string; label: string; parentNodeId: string | null;
  attempts: number; correct: number; accuracy: number; band: string;
}
export async function loadProgress(
  facet: 'domain' | 'skill' | 'micro_skill' = 'skill',
  section?: 'rw' | 'math',
): Promise<ProgressCategory[] | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get('/api/sat/practice/progress', {
      params: { facet, ...(section ? { section } : {}) },
    });
    const cats = res.data?.data?.categories ?? [];
    return cats.map((c: any) => ({
      nodeId: c.node_id, code: c.code, label: c.label, parentNodeId: c.parent_node_id ?? null,
      attempts: c.attempts ?? 0, correct: c.correct ?? 0, accuracy: c.accuracy ?? 0, band: c.band,
    }));
  } catch { return null; }
}

/** The run's #1 weak area, server-ranked (missed desc, accuracy asc). */
export interface DsatTopWeak {
  label: string;
  taxonomyCode: string | null;
  taxonomyNodeId: string | null;
}

/** Server-owned band token derived from the DSAT total (single source of truth). */
export type DsatBand = 'weak' | 'developing' | 'proficient' | 'mastered';

/** Completion status. `failed` is not representable today (no failure marker). */
export type DsatRunStatus = 'completed' | 'in_progress' | 'stale';

export interface DsatRun {
  runId: string;
  examId: string;
  examTitle: string | null;
  totalScore: number | null;
  rwScore: number | null;
  mathScore: number | null;
  completedAt: string | null;
  startedAt: string | null;
  mistakesCount: number | null;   // null = unknown (no graded progress); 0 = clean run
  // Completed-test card enrichment (server-owned — do NOT re-derive client-side):
  topWeak: DsatTopWeak | null;        // null = no graded+tagged evidence
  band: DsatBand | null;              // null = no scored total yet (in-progress)
  status: DsatRunStatus;
  hasRecommendations: boolean;        // gates the secondary "Practice weak skills" CTA
  hasPracticeReport: boolean;         // completed sitting (not freeze-row presence)
}

export async function loadRecentDsatRuns(limit = 5, completedOnly = true, offset = 0): Promise<DsatRun[] | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get('/api/sat/exams/full/runs/recent', {
      params: { limit, completed_only: completedOnly, offset },
    });
    const runs = res.data?.data?.runs ?? [];
    return runs.map((r: any) => ({
      runId:       r.run_id,
      examId:      r.exam_id,
      examTitle:   r.exam_title ?? null,
      totalScore:  r.total_score ?? null,
      rwScore:     r.rw_score ?? null,
      mathScore:   r.math_score ?? null,
      completedAt: r.completed_at ?? null,
      startedAt:   r.started_at ?? null,
      mistakesCount: r.mistakes_count ?? null,
      topWeak: r.top_weak
        ? {
            label:          r.top_weak.label,
            taxonomyCode:   r.top_weak.taxonomyCode ?? null,
            taxonomyNodeId: r.top_weak.taxonomyNodeId ?? null,
          }
        : null,
      band:   (r.band ?? null) as DsatBand | null,
      status: (r.status ?? 'completed') as DsatRunStatus,
      hasRecommendations: Boolean(r.has_recommendations),
      hasPracticeReport: Boolean(r.has_practice_report),
    }));
  } catch { return null; }
}

export interface UserProfile {
  // Honesty: `null` means the user has NOT set a goal — NEVER fabricate a default
  // (e.g. 1450) at the client boundary. Consumers must render a "set a goal"
  // affordance for null rather than presenting a made-up target as the user's.
  targetScore: number | null;
  testDate: string | null;        // ISO yyyy-mm-dd or null
  hoursPerWeek: number | null;
  baselineScore: number | null;
}

export async function saveUserProfile(patch: Partial<UserProfile>): Promise<UserProfile | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const body: Record<string, unknown> = {};
    if (patch.targetScore   != null) body.target_score   = patch.targetScore;
    // `test_date` is nullable. An explicit `testDate: null` in the patch means
    // "clear the date" (the Insights/Profile "Not sure yet (TBD)" path) — it MUST
    // be forwarded as `test_date: null`, not dropped. We therefore gate on key
    // *presence* (`'testDate' in patch`), not on `!= null` (which silently no-ops
    // a TBD-clear because the backend uses `exclude_unset`). See spec §6.1 / §9b.8.
    if ('testDate' in patch)         body.test_date      = patch.testDate ?? null;
    if (patch.hoursPerWeek  != null) body.hours_per_week = patch.hoursPerWeek;
    if (patch.baselineScore != null) body.baseline_score = patch.baselineScore;
    const res = await nextClient.put('/api/sat/user-profile', body);
    const d = res.data?.data;
    if (!d) return null;
    return {
      targetScore:   d.target_score   ?? null,
      testDate:      d.test_date      ?? null,
      hoursPerWeek:  d.hours_per_week ?? null,
      baselineScore: d.baseline_score ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * The single backend predicted score (§8e / §17 D-D). All score surfaces read
 * THIS one value (off `UserKgData.predictedScore`) so HomeV2, HomeV2Desktop, the
 * Insights header, and MapCelebration can never disagree. `GET /insights/score`
 * returns the SAME block the dashboard header carries (one curve, one band).
 */
export interface PredictedScore {
  estimatedScore: number | null;
  rwScore: number | null;
  mathScore: number | null;
  confidenceLow: number | null;
  confidenceHigh: number | null;
  scoreEstimate: ScoreEstimate | null;
  rwScoreEstimate: ScoreEstimate | null;
  mathScoreEstimate: ScoreEstimate | null;
}

/** GET /api/sat/insights/score — the canonical predicted score read by every
 *  score surface. null when unauthenticated / backend error (callers fall back
 *  to the legacy client TS estimate only while this is unavailable). */
export async function loadInsightsScore(): Promise<PredictedScore | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get('/api/sat/insights/score',
      consumeInsightsRefresh() ? { params: { refresh: 1 } } : undefined);
    const d = res.data?.data ?? {};
    const scoreEstimate = adaptScoreEstimate(d.score_estimate ?? d.scoreEstimate);
    const rwScoreEstimate = adaptScoreEstimate(d.rw_score_estimate ?? d.rwScoreEstimate);
    const mathScoreEstimate = adaptScoreEstimate(d.math_score_estimate ?? d.mathScoreEstimate);
    return {
      estimatedScore: estimateValueOrLegacy(scoreEstimate, d.estimated_score ?? d.estimatedScore),
      rwScore:        estimateValueOrLegacy(rwScoreEstimate, d.rw_score ?? d.rwScore),
      mathScore:      estimateValueOrLegacy(mathScoreEstimate, d.math_score ?? d.mathScore),
      confidenceLow:  estimateValueOrLegacy(scoreEstimate ? { ...scoreEstimate, value: scoreEstimate.low } : null, d.confidence_low ?? d.confidenceLow),
      confidenceHigh: estimateValueOrLegacy(scoreEstimate ? { ...scoreEstimate, value: scoreEstimate.high } : null, d.confidence_high ?? d.confidenceHigh),
      scoreEstimate,
      rwScoreEstimate,
      mathScoreEstimate,
    };
  } catch { return null; }
}

export interface UserKgData {
  summary: ApiSummary;
  overlays: Record<string, KgNodeOverlay>;
  edges: KgEdge[];
  recommendations: KgRecommendationItem[];
  profile: UserProfile;
  /** §8e/D-D single backend score; null while loading or when the score endpoint
   *  is unavailable (surfaces fall back to the client TS estimate). */
  predictedScore: PredictedScore | null;
}

// ── transforms (mirror features/kg/api/userKgApi.ts) ───────────────────────

const ACTION_MAP: Record<string, string> = {
  learn: 'practice',
  practice: 'practice',
  review: 'review',
  maintain: 'review',
  prereq_repair: 'prereq_repair',
  pitfall_remediate: 'pitfall_remediate',
};

function toOverlay(n: ApiMapNode): KgNodeOverlay {
  return {
    nodeId: n.node_id,
    nodeType: n.node_type as KgNodeType,
    label: n.label,
    masteryScore: n.overlay.mastery_score,
    confidence: n.overlay.confidence,
    masteryBand: n.overlay.mastery_band as MasteryBand,
    isBlocked: n.overlay.is_blocked,
    blockedByNodeIds: [],
    activePitfall: n.overlay.active_pitfall,
    reviewDue: n.overlay.review_due,
    recentlyImproved: n.overlay.recently_improved,
    priorityScore: n.overlay.priority_score,
  };
}

function toRecommendation(r: ApiRecommendation, idx: number): KgRecommendationItem {
  return {
    id: `rec-${idx}`,
    nodeId: r.node_id,
    label: r.label,
    action: (ACTION_MAP[r.action] ?? r.action) as KgRecommendationItem['action'],
    priorityScore: r.priority_score,
    candidateCount: r.candidateCount,
    hasPracticePool: r.hasPracticePool,
    why: {
      reason: r.why.why_now || r.why.mastery_signal || '',
      signals: [
        ...r.why.pitfalls.map((p) => `Pitfall: ${p}`),
        ...r.why.strategies.map((s) => `Strategy: ${s}`),
        ...r.why.blockers.map((b) => `Blocked by: ${b}`),
        ...r.why.rationale_snippets,
      ].filter(Boolean),
    },
  };
}

// ── public API ─────────────────────────────────────────────────────────────

/**
 * WHOSE data is on screen.
 *
 *  - `'live'`  — this signed-in user's own data, or nothing at all. Every
 *                PRIVATE surface (/today, /practice, /map, /insights, /library
 *                and everything they link to) is `'live'` ALWAYS — including
 *                when the fetch fails. A failure means we render an honest
 *                empty/error state, never someone else's numbers.
 *  - `'demo'`  — the designer's in-bundle marketing/showcase values. ONLY legal
 *                for an anonymous viewer (`status === 'unauthenticated'`), i.e.
 *                a PUBLIC surface. No signed-in code path may read demo data.
 */
export type KgSource = 'live' | 'demo';

/**
 * The fetch lifecycle, made explicit.
 *
 * The old contract was a binary `source: 'live' | 'mock'`, which could not tell
 * "anonymous viewer" apart from "signed-in user whose fetch blew up" — both
 * were `'mock'`. That is precisely how a failed/empty fetch silently painted
 * designer showcase numbers (1280, "Linear inequalities", "+12 mastery") onto a
 * real student's private screens. Private code must be able to distinguish:
 *
 *  - `'loading'`         request in flight — render skeletons, never values.
 *  - `'ready'`           real data for THIS user.
 *  - `'empty'`           the fetch SUCCEEDED and this user genuinely has no KG
 *                        evidence yet (cold start). `data` is attached and is
 *                        truthful (real zeros) — show a calibrating state.
 *  - `'error'`           the fetch FAILED. `data` is null. Show an honest error.
 *                        NEVER substitute demo values here.
 *  - `'unauthenticated'` no usable auth token. The ONLY status that may feed the
 *                        designer demo — and only on a public surface.
 */
export type KgStatus = 'loading' | 'ready' | 'empty' | 'error' | 'unauthenticated';

export interface NextUserKgState {
  status: KgStatus;
  source: KgSource;
  error: string | null;
  data: UserKgData | null;
}

// Boot state: we are a signed-in app (everything under NextApp sits behind
// ProtectedRoute), so the pre-fetch state is `live` + `loading` — NOT `mock`.
// Seeding `mock` here meant the very first paint of every private screen was
// allowed to read designer data.
const initialState: NextUserKgState = {
  status: 'loading',
  source: 'live',
  error: null,
  data: null,
};


export function hasRealAuthToken(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  const token = window.localStorage.getItem('access_token');
  return isAuthLikeToken(token);
}

// Honest empty profile: no goal, no date. targetScore null → consumers show a
// "set your target" affordance instead of a fabricated number.
const DEFAULT_PROFILE: UserProfile = {
  targetScore: null,
  testDate: null,
  hoursPerWeek: null,
  baselineScore: null,
};

async function fetchUserKgFromBackend(): Promise<UserKgData> {
  // /api/sat/user-profile is best-effort — if the endpoint is missing on
  // older backends, we fall back to the same defaults the legacy designer
  // mocks used so HomeV2/InsightsV2 still render.
  // §8e/D-D: load the single backend predicted score alongside the KG fetch so
  // every score surface reads ONE value from `state.data.predictedScore`. Best-
  // effort (null on error) — surfaces fall back to the client TS estimate until
  // it resolves, so the score never renders blank.
  const [summaryRes, mapRes, recsRes, profileRes, predictedScore] = await Promise.all([
    nextClient.get('/api/sat/user-kg/summary'),
    nextClient.get('/api/sat/user-kg/map'),
    nextClient.get('/api/sat/user-kg/recommendations', { params: { limit: 20 } }),
    nextClient.get('/api/sat/user-profile').catch(() => null),
    loadInsightsScore(),
  ]);

  const summary: ApiSummary = summaryRes.data.data;

  const mapNodes: ApiMapNode[] = mapRes.data.data.nodes ?? [];
  const overlays: Record<string, KgNodeOverlay> = {};
  for (const n of mapNodes) overlays[n.node_id] = toOverlay(n);

  const apiEdges: { source: string; target: string; edge_type: string }[] =
    mapRes.data.data.edges ?? [];
  const edges: KgEdge[] = apiEdges.map((e) => ({
    source: e.source,
    target: e.target,
    edgeType: e.edge_type as KgEdgeType,
  }));

  const apiRecs: ApiRecommendation[] = recsRes.data.data.items ?? [];
  const recommendations = apiRecs.map(toRecommendation);

  const profile: UserProfile = profileRes?.data?.data
    ? {
        // Honesty: keep null when the backend has no goal — do NOT fabricate 1450.
        targetScore:   profileRes.data.data.target_score   ?? null,
        testDate:      profileRes.data.data.test_date      ?? null,
        hoursPerWeek:  profileRes.data.data.hours_per_week ?? null,
        baselineScore: profileRes.data.data.baseline_score ?? null,
      }
    : DEFAULT_PROFILE;

  return { summary, overlays, edges, recommendations, profile, predictedScore };
}

/** A number that is present, finite, and actually positive. Guards against the
 *  `0` that `estimateValueOrLegacy` substitutes when a backend omits the score
 *  block entirely — a 0 is "we know nothing", never a measurement (an SAT score
 *  cannot be 0, and a 0 mastery/confidence is exactly the cold-start value). */
function positive(v: unknown): boolean {
  return typeof v === 'number' && Number.isFinite(v) && v > 0;
}

/** An overlay carries EVIDENCE only if it says something about this user beyond
 *  "the node exists". On cold start every field below is the zero value:
 *  `read_service.get_map` synthesizes the whole content KG with
 *  `mastery_band:'unseen'`, `mastery_score:0`, `confidence:0`, `priority_score:0`
 *  and every flag `false`. */
function overlayHasEvidence(o: KgNodeOverlay | undefined | null): boolean {
  if (!o) return false;
  const band = typeof o.masteryBand === 'string' ? o.masteryBand : '';
  if (band !== '' && band !== 'unseen') return true;
  return positive(o.masteryScore)
    || positive(o.confidence)
    || positive(o.priorityScore)
    || o.isBlocked === true
    || o.activePitfall === true
    || o.reviewDue === true
    || o.recentlyImproved === true;
}

/** `get_summary` returns an all-zero / all-null block when there is no
 *  `user_kg_summary_state` row (cold start), so the block's PRESENCE proves
 *  nothing — only a non-zero field does. */
function summaryHasEvidence(s: ApiSummary | undefined | null): boolean {
  if (!s) return false;
  return positive(s.overall_mastery)
    || positive(s.overall_confidence)
    || s.strongest_domain != null
    || s.weakest_domain != null
    || positive(s.active_pitfalls_count)
    || positive(s.review_due_count);
}

/** Cold-start `get_recommendations` serves 20 RANDOM starter concepts from the
 *  content KG — real rows, but zero evidence about the user: they are always
 *  `action:'learn'` (→ `'practice'` after ACTION_MAP) with empty
 *  pitfalls/strategies/blockers/rationale (→ `why.signals === []`). A rec is
 *  evidence only if it carries something the cold-start branch cannot emit: a
 *  non-practice action (review / prereq_repair / pitfall_remediate — those come
 *  only from a committed generation, which requires user state) or any why-signal. */
function recommendationHasEvidence(r: KgRecommendationItem | undefined | null): boolean {
  if (!r) return false;
  if (typeof r.action === 'string' && r.action !== 'practice') return true;
  return (r.why?.signals?.length ?? 0) > 0;
}

/** The backend score is null/absent until there is enough evidence
 *  (`insights_score_block.build_score`: "null on cold start"), but
 *  `loadInsightsScore` returns an OBJECT for any 200 — so `predictedScore != null`
 *  is structure, not evidence. Only a positive value is. */
function scoreHasEvidence(p: PredictedScore | null | undefined): boolean {
  if (!p) return false;
  return positive(p.estimatedScore) || positive(p.rwScore) || positive(p.mathScore);
}

/**
 * Has this user actually DONE anything the backend can speak to?
 *
 * This must be an EVIDENCE test, never a STRUCTURE test. The old predicate
 * (`overlays.length > 0 || recommendations.length > 0 || predictedScore != null`)
 * could never be false in prod, so `status:'empty'` was dead code and every
 * brand-new student was served the `'ready'` path. All three legs are structural:
 *
 *   - `/user-kg/map`             cold start → the CONTENT KG (500 nodes, every
 *                                overlay `unseen`/0) — the curriculum, not the user.
 *   - `/user-kg/recommendations` cold start → 20 random starter concepts.
 *   - `/insights/score`          returns a block on any 200, with null values.
 *
 * So we look INSIDE the payload: a non-`unseen` band, a positive
 * mastery/confidence/priority, a raised flag, a non-zero summary field, a rec the
 * cold-start branch cannot produce, or a positive predicted score. Nothing but
 * zeros ⇒ genuine cold start ⇒ `'empty'` (truthful: show a calibrating state).
 */
function hasKgEvidence(d: UserKgData): boolean {
  if (!d) return false;
  if (Object.values(d.overlays ?? {}).some(overlayHasEvidence)) return true;
  if (summaryHasEvidence(d.summary)) return true;
  if ((d.recommendations ?? []).some(recommendationHasEvidence)) return true;
  return scoreHasEvidence(d.predictedScore);
}

/** Exported for the contract test only — `loadUserKg` is the public surface. */
export const __hasKgEvidenceForTests = hasKgEvidence;

/**
 * Load the signed-in user's KG state. There is deliberately NO mock fallback:
 * see `KgSource` / `KgStatus` above and AGENTS.md § "Data & UI Honesty"
 * (no silent fallbacks — a fallback that substitutes a placeholder when real
 * data is missing is a bug, not a safety net).
 */
export async function loadUserKg(): Promise<NextUserKgState> {
  if (!hasRealAuthToken()) {
    // Public/anonymous only. The `/next/*` tree is behind ProtectedRoute, so a
    // signed-in student cannot legitimately land here; if they somehow do (e.g.
    // the 401 interceptor just cleared a dead token), private screens still show
    // an honest empty state — they no longer read the demo bundle at all.
    return { status: 'unauthenticated', source: 'demo', error: null, data: null };
  }
  try {
    const data = await fetchUserKgFromBackend();
    return {
      status: hasKgEvidence(data) ? 'ready' : 'empty',
      source: 'live',
      error: null,
      data,
    };
  } catch (err) {
    // THE BUG THIS CONTRACT EXISTS TO KILL: this branch used to return
    // `source: 'mock'`, so an authenticated student whose fetch 500'd / timed
    // out saw the designer's showcase numbers rendered as their own progress.
    // A failed fetch is an ERROR, and it is still the LIVE user's surface.
    const message = err instanceof Error ? err.message : 'unknown error';
    return { status: 'error', source: 'live', error: message, data: null };
  }
}

export { initialState };

// ── v2 beta loop: generic TTL cache + recommend/fan-out loaders ─────────────
// The recommend/fanout APIs already return camelCase {data:{...}}, so these
// loaders are thin passthroughs (auth-gated, null on error → designer/mock).

const _recommendationFresh = new Map<string, { at: number; ttl: number; v: RecommendPlan | null }>();
let _requestCacheGeneration = 0;

/** Clear the generic next cache (and the exam-module cache). Call on finish/sign-out.
 *  The no-prefix (full) clear ALSO drops the persisted last-good recommendation
 *  plans, so a new user after sign-out never sees the previous session's plan
 *  flash from SWR before the fresh fetch resolves. */
export function clearNextClientCache(prefix?: string): void {
  _requestCacheGeneration += 1;
  invalidateRequestCache(prefix);
  if (!prefix) {
    _recommendationFresh.clear();
    _moduleCache.clear();
    clearRecommendLastGood();
    return;
  }
  for (const k of _recommendationFresh.keys()) {
    if (k.startsWith(prefix)) _recommendationFresh.delete(k);
  }
}

/**
 * Drop cached recommend reads (fanout candidates + recommendation lists) so the
 * NEXT read recomputes from the backend. Call after the user answers a practice
 * item: the backend already updates pp_user_item_stats + invalidates its own
 * cache, but these client-side entries (60s TTL) would otherwise serve a stale
 * "N you haven't seen" / candidate set when the user returns to the category /
 * recommend page within the TTL window.
 */
export function invalidateRecommendCache(): void {
  clearNextClientCache('fanout_cand:');
  clearNextClientCache('recs:');
}

function cachedGet<T>(key: string, ttlMs: number, fetcher: () => Promise<T>): Promise<T> {
  return cachedRequest(key, ttlMs, fetcher);
}

// ── Recommendation stale-while-revalidate (retry-amplification fix) ──────────
// The /practice "Your plan" section is served by a cold-startable backend query
// with a 6s server statement_timeout that returns a clean 503
// (recommendation_compute_timeout) on miss. The client cap is 8s. Previously a
// cold miss could stack retries (8s × N) into a ~24s blank skeleton before
// erroring. Fix, in three parts:
//   1. SWR: persist the last successful RecommendPlan per key to localStorage so
//      a returning user sees their plan INSTANTLY while we revalidate in the
//      background — a cold miss never blocks the render.
//   2. No retry-on-timeout inside the loaders themselves: retrying a slow cold
//      compute just multiplies the wait without fixing the cause.
//      `loadExamRecommendations`/`loadExamRecommendationsOutcome` and
//      `loadExamRecommendationsSWR` each make exactly ONE network attempt per
//      call and never go through `withRetry`. The one bounded exception lives
//      OUTSIDE these loaders, in the full-page RecommendHome effect only:
//      when the loader REPORTS a transient failure (no response, timeout,
//      5xx, or 429), that effect — and only that effect — may issue ONE
//      additional call after a 1500ms delay. Worst case for that page is
//      8s + 1.5s + 8s = 17.5s and at most two network attempts total; the
//      SWR loader and the inline skill/micro-skill loader keep the strict
//      single-attempt rule with no retry anywhere in their own call path.
//   3. Coherent budgets: the backend 503 is a soft "warming up" signal, not a
//      hard error — we surface last-good (or null) and never retry it into a
//      24s stall.
const RECS_LASTGOOD_PREFIX = 'pp_recs_lastgood:';
// Cap persisted freshness so we never serve a truly ancient plan as "current".
const RECS_LASTGOOD_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h

interface LastGoodEnvelope<T> { at: number; v: T; }

// Track the last-good storage keys we've written this session so we can clear
// them without enumerating localStorage (`.length`/`.key()` aren't available on
// every Storage impl — e.g. the test mock — so we never rely on enumeration).
const _lastGoodKeys = new Set<string>();

function readLastGoodPlan(key: string): RecommendPlan | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(RECS_LASTGOOD_PREFIX + key);
    if (!raw) return null;
    const env = JSON.parse(raw) as LastGoodEnvelope<RecommendPlan>;
    if (!env || typeof env.at !== 'number' || !env.v) return null;
    if (Date.now() - env.at > RECS_LASTGOOD_MAX_AGE_MS) return null;
    return env.v;
  } catch { return null; }
}

function writeLastGoodPlan(key: string, plan: RecommendPlan): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const env: LastGoodEnvelope<RecommendPlan> = { at: Date.now(), v: plan };
    window.localStorage.setItem(RECS_LASTGOOD_PREFIX + key, JSON.stringify(env));
    _lastGoodKeys.add(RECS_LASTGOOD_PREFIX + key);
  } catch { /* quota / serialization — non-fatal, SWR simply won't have a seed */ }
}

/** Clear the persisted last-good recommendation plans (call on sign-out so the
 *  next user never sees the previous session's plan flash before revalidation). */
export function clearRecommendLastGood(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const ls = window.localStorage;
  // Prefer the keys we tracked this session (robust across all Storage impls).
  for (const k of Array.from(_lastGoodKeys)) {
    try { ls.removeItem(k); } catch { /* noop */ }
  }
  _lastGoodKeys.clear();
  // Best-effort belt-and-braces: also sweep any persisted keys from a PRIOR page
  // load (not in our in-memory set) when enumeration is available.
  try {
    if (typeof ls.length === 'number' && typeof ls.key === 'function') {
      const toRemove: string[] = [];
      for (let i = 0; i < ls.length; i++) {
        const k = ls.key(i);
        if (k && k.startsWith(RECS_LASTGOOD_PREFIX)) toRemove.push(k);
      }
      for (const k of toRemove) ls.removeItem(k);
    }
  } catch { /* noop */ }
}

// Is this error the soft, expected "recommendations still warming up" signal —
// i.e. the backend 503 (statement_timeout) or a client-side request timeout?
// Neither should be retried into a multi-attempt stall; both degrade to
// last-good / a gentle warming state.
export function isRecommendationSoftMiss(err: any): boolean {
  const status = axiosStatus(err);
  if (status === 503) return true;                 // backend statement_timeout → clean 503
  if (err?.code === 'ECONNABORTED') return true;   // axios client timeout
  if (status == null && err?.request) return true; // no response (network/timeout)
  return false;
}

/**
 * Is this failure worth exactly ONE more attempt on the full-page plan loader?
 *
 * Transient = the request never got a usable answer: no response (a network
 * abort — bursty around edge deploys), a client-side timeout, a 5xx, or a 429.
 * NOT transient = an answered 4xx (401/403/404) or a non-HTTP throw; retrying
 * those burns the student's time and hides an honest failure.
 *
 * Deliberately STRICTER than the generic `isTransient` above (line 598), which
 * treats every status-less error as retryable: an adapter TypeError is
 * UNKNOWN, not TRANSIENT, and must never be retried.
 */
export function isTransientRecommendFailure(err: any): boolean {
  const status = axiosStatus(err);
  if (status != null) return status >= 500 || status === 429;
  if (err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT') return true;
  return !!err?.request;   // request issued, no response
}

/** Why the plan loader produced no plan. `null` = the request COMPLETED — an
 *  empty payload is an honest empty, not a failure, and is never retried. */
export type RecommendLoadFailure = 'transient' | 'permanent';
export interface RecommendPlanOutcome {
  plan: RecommendPlan | null;
  failure: RecommendLoadFailure | null;
}

// Generated from the backend Pydantic source of truth (DifficultyBucketModel) by
// scripts/gen_recommend_types.py; re-exported so existing imports keep working.
export type { DifficultyBucketBreakdown } from './generated/recommendContract';
import type { DifficultyBucketBreakdown } from './generated/recommendContract';

export interface RecommendCategory {
  rank?: number;
  microSkillNodeId?: string | null;
  microSkillCode: string;
  microSkillLabel: string;
  skillCode?: string | null;
  skillLabel?: string | null;
  domainCode?: string | null;
  domainLabel?: string | null;
  section: string;
  attempted: number;
  missed: number;
  incorrectDensity: number;
  kgMastery: number | null;
  scoreLift?: number | null;
  scoreLiftEstimate?: ScoreEstimate | null;
  pointsToGain: number | null;
  pointsToGainLower: number | null;
  pointsToGainUpper: number | null;
  /** Per-difficulty gap (easy/medium/hard); untested buckets shown neutral. */
  difficultyBreakdown?: DifficultyBucketBreakdown[] | null;
  done: boolean;
  candidateCount?: number;
  hasPracticePool?: boolean;
  practiceHref?: string;
  /** Scope contract (additive; micro rows omit these). */
  scopeType?: 'micro_skill' | 'skill' | null;
  scopeNodeId?: string | null;
  scopeCode?: string | null;
  scopeLabel?: string | null;
  childMicroSkillCount?: number | null;
  childMicroSkillCodes?: string[] | null;
}
export interface RecommendDomain {
  domainCode: string | null;
  domainLabel: string | null;
  categories: RecommendCategory[];
  /** Sum of the domain's skill lifts (additive). */
  potentialLift?: number | null;
  potentialLiftLower?: number | null;
  potentialLiftUpper?: number | null;
  /** Domain-level per-difficulty breakdown (attempts-weighted across its skills). */
  difficultyBreakdown?: DifficultyBucketBreakdown[] | null;
}
export interface ToBeEvaluatedEntry {
  nodeId: string;
  scopeType: 'micro_skill' | 'skill';
  label: string | null;
  section?: string | null;
  /** Scaled-score mass in this scope's UNTESTED display bands (spec 3b). */
  unsurePoints: number;
  untestedBands: string[];
}
export interface RecommendPlan {
  runId: string | null;
  asOfRunId: string | null;
  domains: RecommendDomain[];
  totalPointsRemaining: number;
  notEnoughData: RecommendCategory[];
  noEvidenceReason: string | null;
  scoringMethod: string;
  /** v2 "To be evaluated" (score-estimator-v2 spec 3b): scopes with unassessed
   *  official mass, ranked by unsurePoints desc. Rendered with a DISABLED
   *  placeholder Evaluate button (interaction deferred). Present (possibly
   *  empty) on every backend payload. */
  toBeEvaluated?: ToBeEvaluatedEntry[];
}
export interface FanoutCandidateItem {
  itemContentId: string;
  stemPreview: string;
  difficulty: number | null;
  seen: boolean;
}
export interface FanoutKnowledge {
  concepts: Array<{ name?: string; description?: string }>;
  pitfalls: Array<{ name?: string; whyItHappens?: string; howToAvoid?: string }>;
  strategies: Array<{ name?: string; whenToUse?: string }>;
}
export interface FanoutPerformanceStat {
  attempted: number;
  correct: number;
  accuracy: number | null;
}
export interface FanoutRecentPerformanceStat extends FanoutPerformanceStat {
  window: number;
}
export interface FanoutPracticeGoalBand {
  difficulty: string;
  practicePoolCount: number;
  window: number;
  targetAccuracy: number;
  targetDelta?: number | null;
  weightedGap?: number | null;
  weightedGapShare?: number | null;
  recentAttempted: number;
  recentCorrect: number;
  recentAccuracy: number | null;
  achieved: boolean;
}
export interface FanoutCandidates {
  types: string[];
  seed: number | null;
  total: number;
  new?: number;
  seen?: number;
  difficulty?: { easy: number; medium: number; hard: number; unrated: number };
  knowledge?: FanoutKnowledge;
  practice?: { answered: number; attempts: number; correct: number; accuracy: number | null };
  performanceByDifficulty?: Record<string, FanoutPerformanceStat>;
  recentPerformanceByDifficulty?: Record<string, FanoutRecentPerformanceStat>;
  officialTestByDifficulty?: Record<string, { slots: number; questionsPerTest: number; share: number }>;
  practiceGoal?: { accuracyThreshold: number; confidenceThreshold?: number; bands: FanoutPracticeGoalBand[] };
  testFrequencyPerExam?: number | null;
  limit: number;
  offset: number;
  items: FanoutCandidateItem[];
}

/** Deep snake_case -> camelCase that CARRIES EVERY KEY THROUGH. The recommend
 * adapters spread this first so a new backend field can never be silently dropped
 * (the bug that hid difficultyBreakdown/potentialLift); explicit lines below only
 * add required-field defaults and the ScoreEstimate normalization. */
function deepCamelize(v: any): any {
  if (Array.isArray(v)) return v.map(deepCamelize);
  if (v && typeof v === 'object') {
    const out: any = {};
    for (const [k, val] of Object.entries(v)) {
      out[k.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase())] = deepCamelize(val);
    }
    return out;
  }
  return v;
}

function adaptRecommendCategoryPayload(c: any): RecommendCategory {
  const x = deepCamelize(c);  // carries difficultyBreakdown + any future field
  const scoreLiftEstimate = adaptScoreEstimate(c.score_lift_estimate ?? c.scoreLiftEstimate);
  const scoreLift = estimateValueOrLegacy(scoreLiftEstimate, x.scoreLift ?? x.pointsToGain);
  return {
    ...x,
    rank: x.rank ?? undefined,
    microSkillNodeId: x.microSkillNodeId ?? null,
    microSkillCode: x.microSkillCode ?? '',
    microSkillLabel: x.microSkillLabel ?? '',
    skillCode: x.skillCode ?? null,
    skillLabel: x.skillLabel ?? null,
    domainCode: x.domainCode ?? null,
    domainLabel: x.domainLabel ?? null,
    section: x.section ?? '',
    attempted: x.attempted ?? 0,
    missed: x.missed ?? 0,
    incorrectDensity: x.incorrectDensity ?? 0,
    kgMastery: x.kgMastery ?? null,
    scoreLift,
    scoreLiftEstimate,
    pointsToGain: scoreLiftEstimate ? scoreLiftEstimate.value : optionalNumber(x.pointsToGain ?? scoreLift),
    pointsToGainLower: scoreLiftEstimate ? scoreLiftEstimate.low : optionalNumber(x.pointsToGainLower ?? scoreLift),
    pointsToGainUpper: scoreLiftEstimate ? scoreLiftEstimate.high : optionalNumber(x.pointsToGainUpper ?? scoreLift),
    done: !!(x.done ?? false),
    // Scope fields pass through deepCamelize; normalize for the typed contract.
    scopeType: x.scopeType ?? null,
    scopeNodeId: x.scopeNodeId ?? null,
    scopeCode: x.scopeCode ?? null,
    scopeLabel: x.scopeLabel ?? null,
    childMicroSkillCount: x.childMicroSkillCount ?? null,
    childMicroSkillCodes: x.childMicroSkillCodes ?? null,
  };
}

function adaptRecommendDomainPayload(d: any): RecommendDomain {
  const x = deepCamelize(d);  // carries potentialLift + difficultyBreakdown + future fields
  return {
    ...x,
    domainCode: x.domainCode ?? null,
    domainLabel: x.domainLabel ?? null,
    categories: (d.categories ?? []).map(adaptRecommendCategoryPayload),
  };
}

function adaptRecommendPlanPayload(raw: any): RecommendPlan | null {
  if (!raw || typeof raw !== 'object') return null;
  return {
    // Pass-through base (deepCamelize): the backend RecPlanModel shape IS the
    // contract — plan-level additions (e.g. the v2 `toBeEvaluated` section,
    // score-estimator-v2 spec 3b) must survive the adapter un-dropped.
    ...deepCamelize(raw),
    runId: raw.run_id ?? raw.runId ?? null,
    asOfRunId: raw.as_of_run_id ?? raw.asOfRunId ?? null,
    domains: (raw.domains ?? []).map(adaptRecommendDomainPayload),
    totalPointsRemaining: legacyNumber(raw.total_points_remaining ?? raw.totalPointsRemaining),
    notEnoughData: (raw.not_enough_data ?? raw.notEnoughData ?? []).map(adaptRecommendCategoryPayload),
    noEvidenceReason: raw.no_evidence_reason ?? raw.noEvidenceReason ?? null,
    scoringMethod: raw.scoring_method ?? raw.scoringMethod ?? 'estimated_avg_sheets',
  };
}

/** Ranked weak micro-skills (or skills, when opts.scope='skill') for a run, or
 *  the latest completed run when runId omitted. `scope` defaults to 'micro_skill';
 *  omitting it sends the exact same request as before (backward compatible). The
 *  attempt-driven endpoint serves skill scope; the run-scoped endpoint does not in
 *  slice 1, so scope is only sent on the attempt-driven path. */
export async function loadExamRecommendationsOutcome(
  runId?: string, section?: 'rw' | 'math', opts?: { scope?: 'micro_skill' | 'skill' },
): Promise<RecommendPlanOutcome> {
  if (!hasRealAuthToken()) return { plan: null, failure: 'permanent' };
  const scope = opts?.scope ?? 'micro_skill';
  const path = runId
    ? `/api/sat/exams/full/runs/${runId}/recommendations`
    : '/api/sat/recommendations/categories';
  const params: any = {};
  if (section) params.section = section;
  if (!runId && scope !== 'micro_skill') params.scope = scope;  // omit default → identical request
  const key = `recs:${runId || 'latest'}:${section || 'all'}:${scope}`;
  const generation = _requestCacheGeneration;
  try {
    // Plain single-attempt fetch (NO retry-on-timeout — the amplification fix — and
    // NO stale-while-revalidate). This is the full /practice/recommend page loader
    // (RecommendHome + its skill↔micro child back-fill). It deliberately does NOT
    // read/write the SWR localStorage last-good: that persisted plan survives the
    // e2e backend cache-bust and would serve a PRIOR fixture's rows, breaking the
    // skills-tab fixture isolation. SWR belongs ONLY on the /practice inline
    // "Your plan" surface (loadExamRecommendationsSWR), where a cold miss must not
    // block the launcher. A cold miss here still yields null → the page's own
    // error state, which already reads "try again in a moment".
    // The ONE bounded retry lives in the page effect (RecommendHome.tsx), never
    // here: only the component can cancel it on unmount / a deps change. This
    // loader itself still makes exactly one attempt; the note above (line 1944)
    // was amended in this change to define that bounded page-level retry, not
    // to claim this loader "allows" it.
    const plan = await cachedGet(key, 60_000, async () => {
      const res = await nextClient.get(path, { params });
      return adaptRecommendPlanPayload(res.data?.data ?? null);
    });
    if (generation === _requestCacheGeneration) {
      _recommendationFresh.set(key, { at: Date.now(), ttl: 60_000, v: plan });
    }
    return { plan, failure: null };
  } catch (err) {
    return { plan: null, failure: isTransientRecommendFailure(err) ? 'transient' : 'permanent' };
  }
}

/** Back-compat null-collapsing view of the loader above: unchanged contract for
 *  every existing caller. New callers should use the outcome form so a FAILED
 *  load stays distinguishable from an EMPTY one. */
export async function loadExamRecommendations(
  runId?: string, section?: 'rw' | 'math', opts?: { scope?: 'micro_skill' | 'skill' },
): Promise<RecommendPlan | null> {
  return (await loadExamRecommendationsOutcome(runId, section, opts)).plan;
}

/**
 * Freshness-aware outcome for the recommendation plan loader.
 *   - `fresh`     → served from a completed network fetch this call.
 *   - `stale`     → served instantly from the persisted last-good plan while a
 *                   background revalidation is in flight (SWR); `plan` is set.
 *   - `warming`   → no last-good to show and the backend is still computing
 *                   (503 / timeout); `plan` is null → render a soft "warming up"
 *                   state, NOT a hard error.
 *   - `empty`     → the fetch succeeded but returned no plan (null payload).
 *   - `error`     → a genuine, non-soft failure and no last-good; `plan` null.
 */
export type RecommendFreshness = 'fresh' | 'stale' | 'warming' | 'empty' | 'error';
export interface RecommendPlanResult {
  plan: RecommendPlan | null;
  freshness: RecommendFreshness;
  /** True while a background SWR revalidation is running (freshness==='stale'). */
  revalidating: boolean;
}

/**
 * Stale-while-revalidate recommendation-plan loader (retry-amplification fix).
 *
 * Ordering:
 *   1. In-memory 60s cache hit → return `fresh` immediately.
 *   2. Otherwise fire exactly ONE network attempt (no `withRetry`; the 8s client
 *      timeout is a single budget, coherent with the backend 6s statement_timeout
 *      that returns a clean 503).
 *   3. If a persisted last-good plan exists, return it as `stale` IMMEDIATELY and
 *      let the single network attempt revalidate in the background — the user is
 *      never blocked on a cold miss.
 *   4. On a soft miss (503 / client timeout / network) with no last-good, return
 *      `warming` (plan null) so the UI shows a gentle "still computing" state,
 *      never a 24s skeleton or hard error, and never a retry storm.
 */
export async function loadExamRecommendationsSWR(
  runId?: string, section?: 'rw' | 'math', opts?: { scope?: 'micro_skill' | 'skill' },
): Promise<RecommendPlanResult> {
  if (!hasRealAuthToken()) return { plan: null, freshness: 'error', revalidating: false };
  const scope = opts?.scope ?? 'micro_skill';
  const path = runId
    ? `/api/sat/exams/full/runs/${runId}/recommendations`
    : '/api/sat/recommendations/categories';
  const params: any = {};
  if (section) params.section = section;
  if (!runId && scope !== 'micro_skill') params.scope = scope;  // omit default → identical request
  const key = `recs:${runId || 'latest'}:${section || 'all'}:${scope}`;

  // 1. Warm in-memory cache → fresh, no network.
  const mem = _recommendationFresh.get(key);
  if (mem && Date.now() - mem.at < mem.ttl) {
    return { plan: mem.v, freshness: 'fresh', revalidating: false };
  }

  // A single network attempt — no retry-on-timeout in this loader, ever. Retrying
  // a slow cold compute only multiplies the wait (8s×N) without fixing the cause.
  // This SWR loader keeps the strict single-attempt rule; only the full-page
  // RecommendHome effect issues a bounded second call, and only after a
  // transient failure (see the note at line 1944).
  const generation = _requestCacheGeneration;
  const fetchOnce = (): Promise<RecommendPlan | null> => {
    return cachedRequest(key, 60_000, async () => {
      const res = await nextClient.get(path, { params });
      return adaptRecommendPlanPayload(res.data?.data ?? null);
    }).then((plan) => {
      // A completion detached by sign-out/prefix invalidation may still resolve
      // for its original caller, but it must not seed freshness or last-good for
      // the next account.
      if (generation === _requestCacheGeneration) {
        _recommendationFresh.set(key, { at: Date.now(), ttl: 60_000, v: plan });
        if (plan) writeLastGoodPlan(key, plan);
      }
      return plan;
    });
  };

  // 3. Serve last-good instantly + revalidate in the background (SWR).
  const lastGood = readLastGoodPlan(key);
  if (lastGood) {
    // Fire-and-forget: cachedRequest coalesces callers for this key. A soft miss
    // leaves last-good in place; a real failure is swallowed because we already
    // have an honest value to show.
    void fetchOnce().catch(() => null);
    return { plan: lastGood, freshness: 'stale', revalidating: true };
  }

  // 4. Cold path with nothing to show: one attempt, then classify.
  try {
    const plan = await fetchOnce();
    return { plan, freshness: plan ? 'fresh' : 'empty', revalidating: false };
  } catch (err) {
    if (isRecommendationSoftMiss(err)) {
      // Backend still computing / timed out — soft "warming up", not a hard error.
      return { plan: null, freshness: 'warming', revalidating: false };
    }
    return { plan: null, freshness: 'error', revalidating: false };
  }
}

/** Fan-out categories (ranked micro-skills + candidateCount + practiceHref). */
export async function loadFanoutCategories(runId?: string): Promise<RecommendPlan | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get('/api/sat/practice/fanout', {
      params: { mode: 'categories', run_id: runId },
    });
    return adaptRecommendPlanPayload(res.data?.data ?? null);
  } catch { return null; }
}

/** Fan-out candidate items for one or more micro-skill codes (taxonomy-filter preview). */
export async function loadFanoutCandidates(
  types: string[],
  opts: { limit?: number; offset?: number; seed?: number; runId?: string; difficultyBands?: string[] } = {},
): Promise<FanoutCandidates | null> {
  if (!hasRealAuthToken() || !types.length) return null;
  const limit = opts.limit ?? 20, offset = opts.offset ?? 0;
  const difficultyBands = (opts.difficultyBands ?? []).map((b) => b.trim().toLowerCase()).filter(Boolean);
  const difficultyBandsParam = difficultyBands.length ? difficultyBands.join(',') : undefined;
  // Cached (60s) so RecommendHome can PREFETCH the top category's candidates and
  // the category drill-down is served instantly from this in-memory cache (same
  // key as CategoryDetail's fetch). cachedGet also dedups concurrent calls.
  const key = `fanout_cand:${[...types].sort().join(',')}:${limit}:${offset}:${opts.seed ?? ''}:${opts.runId ?? ''}:${difficultyBandsParam ?? ''}`;
  try {
    return await cachedGet(key, 60_000, async () => {
      const res = await nextClient.get('/api/sat/practice/fanout', {
        params: {
          mode: 'candidates', types: types.join(','),
          limit, offset, seed: opts.seed, run_id: opts.runId, difficulty_bands: difficultyBandsParam,
        },
      });
      return (res.data?.data ?? null) as FanoutCandidates | null;
    });
  } catch { return null; }
}

// ── v2 Insights page (§8c endpoints) ───────────────────────────────────────
// Typed fetchers for the four Insights read endpoints. Each is auth-gated
// (null when !hasRealAuthToken()). Dashboard/activity errors return null so the
// page can fall back to cold-start states; trend errors throw so the trajectory
// card's local error + Retry state is reachable. All accept an optional
// `{ refresh }` → `?refresh=1` to bust the §8b 1-day server cache.

/** Shared `{ refresh?: boolean }` option bag → `params.refresh = 1` when true. */
export interface InsightsFetchOpts { refresh?: boolean }
// One-shot: AuthContext.logout() sets `pp_insights_refresh`; the first Insights
// read after the next login consumes it (here) so that read passes ?refresh=1,
// busting the server-side `v2_insights:<uid>:` cache prefix. So a log-out →
// log-in never shows the previous session's cached score. SIDE-EFFECTING: reads
// AND clears the flag — resolve it once per request (see resolveRefresh), never
// speculatively. Consumed once (the prefix-bust covers all Insights sub-keys).
function consumeInsightsRefresh(): boolean {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('pp_insights_refresh') === '1') {
      localStorage.removeItem('pp_insights_refresh');
      return true;
    }
  } catch { /* noop */ }
  return false;
}

/** The effective refresh for one Insights request: the caller's explicit opt, or
 *  the one-shot post-login flag (consumed). Call exactly once per request. */
function resolveRefresh(opts?: InsightsFetchOpts): boolean {
  return Boolean(opts?.refresh) || consumeInsightsRefresh();
}

// Pure: maps an already-resolved refresh boolean to query params.
function refreshParams(refresh: boolean): Record<string, unknown> {
  return refresh ? { refresh: 1 } : {};
}

/**
 * A flattened priority row (§8c). These are the verbatim `RecommendCategory`
 * fields the `/recommendations/categories` engine emits (the dashboard backend
 * flattens `domains[].categories[]` and sorts by `pointsToGain` desc), PLUS the
 * two Insights-computed §8c/D-A fields the engine does NOT emit:
 *   - `provisional` — backend Wilson band-gate (thin lifetime evidence).
 *   - `lifetimeAttempts` — lifetime `n` for the provisional gate + "9/20" denominator.
 * No `masteryBand` (client derives it from `kgMastery` via `masteryBand()`, §7)
 * and no `practiceHref` (FE synthesizes the deep-link via `practiceSkillHref()`).
 */
export interface InsightPriority {
  rank?: number;
  microSkillNodeId: string | null;  // node UUID → recommendation_clicked.taxonomyNodeId (REQUIRED)
  microSkillCode: string;
  microSkillLabel: string;
  skillCode: string | null;
  skillLabel: string | null;
  domainCode: string | null;
  domainLabel: string | null;
  section: string;
  attempted: number;
  missed: number;
  incorrectDensity: number;
  kgMastery: number | null;
  scoreLift?: number | null;
  scoreLiftEstimate?: ScoreEstimate | null;
  pointsToGain: number | null;
  pointsToGainLower: number | null;
  pointsToGainUpper: number | null;
  done: boolean;
  candidateCount?: number;
  hasPracticePool?: boolean;
  provisional: boolean;       // §8c D-A
  lifetimeAttempts: number;   // §8c D-A
  lifetimeCorrect: number;    // §8c D-A — real lifetime correct count (chip numerator)
}

export interface InsightsHeader {
  estimatedScore: number | null;
  rwScore: number | null;
  mathScore: number | null;
  mastery: number | null;
  confidenceLow: number | null;
  confidenceHigh: number | null;
  scoreEstimate: ScoreEstimate | null;
  rwScoreEstimate: ScoreEstimate | null;
  mathScoreEstimate: ScoreEstimate | null;
  // Honesty: null = the user has not set a goal (never a fabricated 1450). A
  // null target also means pointsRemaining is unknown (null), not 0.
  targetScore: number | null;
  pointsRemaining: number | null;
  daysToTest: number | null;
  deltaVsPriorN: number | null;
}

export interface InsightRecentClear {
  taxonomyNodeId: string | null;
  label: string;
  microSkillCode: string | null;
  pointsSecured: number;
}

export interface InsightsDashboard {
  header: InsightsHeader;
  topPriorities: InsightPriority[];
  weakSkillsCount: number;
  notEnoughData: boolean;
  noEvidenceReason: string | null;
  recentActivity: HistoryItem[];
  recentClears: InsightRecentClear[];
}

// snake_case → camelCase for a flattened priority row, tolerating either form
// (the dashboard backend may serialize verbatim engine camelCase or snake_case).
function adaptInsightPriority(r: any): InsightPriority {
  const scoreLiftEstimate = adaptScoreEstimate(r.score_lift_estimate ?? r.scoreLiftEstimate);
  const scoreLift = estimateValueOrLegacy(scoreLiftEstimate, r.score_lift ?? r.scoreLift ?? r.points_to_gain ?? r.pointsToGain);
  return {
    rank:              r.rank ?? undefined,
    microSkillNodeId:  r.micro_skill_node_id ?? r.microSkillNodeId ?? null,
    microSkillCode:    r.micro_skill_code    ?? r.microSkillCode    ?? '',
    microSkillLabel:   r.micro_skill_label   ?? r.microSkillLabel   ?? '',
    skillCode:         r.skill_code          ?? r.skillCode          ?? null,
    skillLabel:        r.skill_label         ?? r.skillLabel         ?? null,
    domainCode:        r.domain_code         ?? r.domainCode         ?? null,
    domainLabel:       r.domain_label        ?? r.domainLabel        ?? null,
    section:           r.section ?? '',
    attempted:         r.attempted ?? 0,
    missed:            r.missed ?? 0,
    incorrectDensity:  r.incorrect_density   ?? r.incorrectDensity   ?? 0,
    kgMastery:         r.kg_mastery          ?? r.kgMastery          ?? null,
    scoreLift,
    scoreLiftEstimate,
    pointsToGain:      scoreLiftEstimate ? scoreLiftEstimate.value : optionalNumber(r.points_to_gain ?? r.pointsToGain ?? scoreLift),
    pointsToGainLower: scoreLiftEstimate ? scoreLiftEstimate.low : optionalNumber(r.points_to_gain_lower ?? r.pointsToGainLower ?? scoreLift),
    pointsToGainUpper: scoreLiftEstimate ? scoreLiftEstimate.high : optionalNumber(r.points_to_gain_upper ?? r.pointsToGainUpper ?? scoreLift),
    done:              !!(r.done ?? false),
    candidateCount:    r.candidate_count     ?? r.candidateCount,
    hasPracticePool:   r.has_practice_pool   ?? r.hasPracticePool,
    provisional:       !!(r.provisional ?? false),
    lifetimeAttempts:  r.lifetime_attempts   ?? r.lifetimeAttempts   ?? 0,
    lifetimeCorrect:   r.lifetime_correct    ?? r.lifetimeCorrect    ?? 0,
  };
}

function adaptInsightRecentClear(r: any): InsightRecentClear {
  return {
    taxonomyNodeId: r.taxonomy_node_id ?? r.taxonomyNodeId ?? null,
    label: r.label ?? 'Skill',
    microSkillCode: r.micro_skill_code ?? r.microSkillCode ?? null,
    pointsSecured: Number(r.points_secured ?? r.pointsSecured ?? 0),
  };
}

// Reuse loadHistory's row shape so recentActivity items match the History list.
function adaptHistoryItem(r: any): HistoryItem {
  return {
    attemptId: r.attempt_id, timestamp: r.timestamp ?? null, timeSpentMs: r.time_spent_ms ?? null,
    isCorrect: !!r.is_correct,
    selectedOption: r.selected_option ?? null, enteredAnswer: r.entered_answer ?? null,
    answerType: r.answer_type ?? null, source: r.source ?? null, mode: r.mode ?? null,
    testRunId: r.test_run_id ?? null, itemContentId: r.item_content_id,
    stem: r.stem ?? null, choices: r.choices ?? [], answerKey: r.answer_key ?? null,
    skill: r.skill ? { code: r.skill.code, label: r.skill.label, domain: r.skill.domain ?? null } : null,
  };
}

/**
 * GET /api/sat/insights/dashboard — one-call page load (header + priorities + recent).
 *
 * FAILURE IS RETHROWN, not swallowed. This function used to `catch { return null }`,
 * and `null` is also what a successful-but-not-signed-in call returns — so the
 * caller could not tell a 500 from "no data", and useInsightsData rendered a total
 * outage as a cold start ("Set your goal", "Answer a few practice questions to get
 * your first priorities") to students with months of history. A loader that hides a
 * failure forces every consumer to fabricate an explanation for the missing data.
 * Same contract as loadScoreTrend/loadWeakTrend below: null ⇒ no auth token; throw
 * ⇒ the request failed; a value ⇒ a real answer (possibly an empty one).
 */
export async function loadInsightsDashboard(opts?: InsightsFetchOpts): Promise<InsightsDashboard | null> {
  if (!hasRealAuthToken()) return null;
  const res = await nextClient.get('/api/sat/insights/dashboard', { params: refreshParams(resolveRefresh(opts)) });
    const d = res.data?.data ?? {};
    const h = d.header ?? {};
    const scoreEstimate = adaptScoreEstimate(h.score_estimate ?? h.scoreEstimate);
    const rwScoreEstimate = adaptScoreEstimate(h.rw_score_estimate ?? h.rwScoreEstimate);
    const mathScoreEstimate = adaptScoreEstimate(h.math_score_estimate ?? h.mathScoreEstimate);
    return {
      header: {
        estimatedScore: estimateValueOrLegacy(scoreEstimate, h.estimated_score ?? h.estimatedScore),
        rwScore:        estimateValueOrLegacy(rwScoreEstimate, h.rw_score ?? h.rwScore),
        mathScore:      estimateValueOrLegacy(mathScoreEstimate, h.math_score ?? h.mathScore),
        mastery:        optionalNumber(h.mastery),
        confidenceLow:  scoreEstimate ? scoreEstimate.low : legacyNumber(h.confidence_low ?? h.confidenceLow),
        confidenceHigh: scoreEstimate ? scoreEstimate.high : legacyNumber(h.confidence_high ?? h.confidenceHigh),
        scoreEstimate,
        rwScoreEstimate,
        mathScoreEstimate,
        targetScore:    h.target_score    ?? h.targetScore    ?? null,
        pointsRemaining: h.points_remaining ?? h.pointsRemaining ?? null,
        daysToTest:     h.days_to_test    ?? h.daysToTest    ?? null,
        deltaVsPriorN:  h.delta_vs_prior_n ?? h.deltaVsPriorN ?? null,
      },
      topPriorities:   (d.top_priorities ?? d.topPriorities ?? []).map(adaptInsightPriority),
      weakSkillsCount: d.weak_skills_count ?? d.weakSkillsCount ?? 0,
      notEnoughData:   !!(d.not_enough_data ?? d.notEnoughData ?? false),
      noEvidenceReason: d.no_evidence_reason ?? d.noEvidenceReason ?? null,
      recentActivity:  (d.recent_activity ?? d.recentActivity ?? []).map(adaptHistoryItem),
      recentClears:    (d.recent_clears ?? d.recentClears ?? []).map(adaptInsightRecentClear),
    };
}

export interface ScoreTrendPoint { date: string; estimatedScore: number }
export interface ScoreTrendAnchor { date: string; totalScore: number; runId: string }
export interface ScoreTrend {
  points: ScoreTrendPoint[];
  examAnchors: ScoreTrendAnchor[];
  // Honesty: null = no goal set → the chart draws no goal line (never a fake 1450).
  goal: number | null;
}

/** GET /api/sat/insights/score-trend?range= — trajectory line + exam anchors + goal. */
export async function loadScoreTrend(
  range: '30d' | 'all', opts?: InsightsFetchOpts,
): Promise<ScoreTrend | null> {
  if (!hasRealAuthToken()) return null;
  const res = await nextClient.get('/api/sat/insights/score-trend', {
    params: { range, ...refreshParams(resolveRefresh(opts)) },
  });
  const d = res.data?.data ?? {};
  return {
    points: (d.points ?? []).map((p: any) => ({
      date: p.date,
      estimatedScore: p.estimated_score ?? p.estimatedScore ?? 0,
    })),
    examAnchors: (d.exam_anchors ?? d.examAnchors ?? []).map((a: any) => ({
      date: a.date,
      totalScore: a.total_score ?? a.totalScore ?? 0,
      runId: a.run_id ?? a.runId ?? '',
    })),
    goal: d.goal ?? null,
  };
}

export interface ActivityDay {
  date: string;
  correct: number;
  incorrect: number;
  skillsPracticed: number;
  pointsSecured: number;
}
export interface ActivityDaily { days: ActivityDay[] }

/**
 * GET /api/sat/insights/activity-daily?days= — per-day correct/incorrect/skills/points.
 * Rethrows on failure (see loadInsightsDashboard): a swallowed rejection here read as
 * "Answer a few questions to start your streak" on a dead backend.
 */
export async function loadActivityDaily(
  days: number, opts?: InsightsFetchOpts,
): Promise<ActivityDaily | null> {
  if (!hasRealAuthToken()) return null;
  const res = await nextClient.get('/api/sat/insights/activity-daily', {
    params: { days, ...refreshParams(resolveRefresh(opts)) },
  });
  const d = res.data?.data ?? {};
  return {
    days: (d.days ?? []).map((x: any) => ({
      date: x.date,
      correct: x.correct ?? 0,
      incorrect: x.incorrect ?? 0,
      skillsPracticed: x.skills_practiced ?? x.skillsPracticed ?? 0,
      pointsSecured: x.points_secured ?? x.pointsSecured ?? 0,
    })),
  };
}

export interface WeakTrendPoint { date: string; weakSkillsCount: number }
export interface WeakTrend { points: WeakTrendPoint[] }

/** GET /api/sat/insights/weak-trend?range= — reconstructed weak-skill count series. */
export async function loadWeakTrend(
  range: '30d' | 'all', opts?: InsightsFetchOpts,
): Promise<WeakTrend | null> {
  if (!hasRealAuthToken()) return null;
  const res = await nextClient.get('/api/sat/insights/weak-trend', {
    params: { range, ...refreshParams(resolveRefresh(opts)) },
  });
  const d = res.data?.data ?? {};
  return {
    points: (d.points ?? []).map((p: any) => ({
      date: p.date,
      weakSkillsCount: p.weak_skills_count ?? p.weakSkillsCount ?? 0,
    })),
  };
}

// ── Daily progress recap (docs/canonical/progress-recap.md) ──────────────────

export interface RecapEffort {
  answeredCount: number;
  activeDays: number;
  recordedMinutes: number | null;
  timeCoverageComplete: boolean;
}

export interface RecapData {
  status: 'card' | 'quiet' | 'no_activity';
  reason?: string;
  recapId?: string;
  welcomeBack?: boolean;
  headline?: any | null;
  secondary?: any | null;
  effort?: RecapEffort;
  analysisFailed?: boolean;
  throughAttemptId?: number | null;
  throughAttemptTs?: number | null;
}

/** First-visit recap evaluation. null = fetch failed (FE renders nothing —
 * absence, never a fabricated state). */
export async function loadRecap(): Promise<RecapData | null> {
  if (!hasRealAuthToken()) return null;
  try {
    const res = await nextClient.get('/api/sat/insights/recap');
    return (res.data?.data ?? null) as RecapData | null;
  } catch { return null; }
}

/** Acknowledge (seen|dismiss). True on success; the card treats a failed ack as
 * ack-failed state (card stays, retry affordance) — never silently advances. */
export async function ackRecap(recap: RecapData, action: 'seen' | 'dismiss'): Promise<boolean> {
  if (!hasRealAuthToken() || !recap.recapId) return false;
  try {
    const res = await nextClient.post('/api/sat/insights/recap/ack', {
      recapId: recap.recapId,
      action,
      // Telemetry only (celebration-type/dismissal diagnostics) — the server
      // derives the dismiss checkpoint itself and never trusts client state.
      headlineKind: recap.headline?.kind
        ?? (recap.analysisFailed ? 'analysis_failed' : 'effort_only'),
    });
    return res.data?.data?.status === 'ok';
  } catch { return false; }
}
